import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/print.css';
import { exportServices } from '../lib/exportService';
import { Template1 } from './templates/Template1';
import { Template2 } from './templates/Template2';
import { Template3 } from './templates/Template3';
import { Template4 } from './templates/Template4';
import { Template5 } from './templates/Template5';
import { Template6 } from './templates/Template6';
import { Template7 } from './templates/Template7';
import { Template8 } from './templates/Template8';
import { CVData } from '../types';
import { TrendingUp, Target, Book, Award, Zap, ChevronDown, ChevronUp, Star, StarHalf, Trophy, FileText, ImagePlus, Globe, Maximize2, X, ZoomIn, ZoomOut, MessageCircle, Send, User, Bot, Edit3, Save, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getChatbotResponse } from '../lib/groq';
// Importar componentes de esqueleto
import AnalysisSkeleton from './AnalysisSkeleton';
import CoverLetterSkeleton from './CoverLetterSkeleton';
import ImageCropper from './ImageCropper';

// Constantes para los diferentes casos de distribución
const DISTRIBUTION_CASES = {
  CASE_1: 'case_1', // 1 experiencia: todo en una página
  CASE_2: 'case_2', // 2 experiencias: primera página con experiencias, segunda con cursos
  CASE_3: 'case_3', // 3 experiencias: primera página con 2 experiencias, segunda con 1 experiencia y cursos
  CASE_4: 'case_4', // 4 experiencias: primera página con 2 experiencias, segunda con 2 experiencias y cursos
  CASE_5: 'case_5', // 5 experiencias: primera página con 2 experiencias, segunda con 3 experiencias, tercera con cursos
  CASE_6: 'case_6', // 6 experiencias: primera página con 2 experiencias, segunda con 3 experiencias, tercera con 1 experiencia y cursos
  CASE_7: 'case_7', // 7 experiencias: primera página con 2 experiencias, segunda con 3 experiencias, tercera con 2 experiencias y cursos
  CASE_8: 'case_8'  // 8 experiencias: primera página con 2 experiencias, segunda con 3 experiencias, tercera con 3 experiencias y cursos
};

const templates = {
  template1: Template1,
  template2: Template2,
  template3: Template3,
  template4: Template4,
  template5: Template5,
  template6: Template6,
  template7: Template7,
  template8: Template8
} as const;

// Opciones de fuentes disponibles
const FONT_OPTIONS = [
  { value: 'Arial', label: 'Arial', style: 'Arial, sans-serif' },
  { value: 'Helvetica', label: 'Helvetica', style: 'Helvetica, Arial, sans-serif' },
  { value: 'Times', label: 'Times New Roman', style: 'Times, "Times New Roman", serif' },
  { value: 'Calibri', label: 'Calibri', style: 'Calibri, Arial, sans-serif' },
  { value: 'Georgia', label: 'Georgia', style: 'Georgia, serif' },
  { value: 'Verdana', label: 'Verdana', style: 'Verdana, Geneva, sans-serif' },
  { value: 'Roboto', label: 'Roboto', style: '"Roboto", Arial, sans-serif' },
  { value: 'OpenSans', label: 'Open Sans', style: '"Open Sans", Arial, sans-serif' }
];

const CATEGORY_ICONS = {
  'Habilidades Técnicas': <Zap className="w-6 h-6 text-amber-500" />,
  'Certificaciones': <Award className="w-6 h-6 text-blue-500" />,
  'Experiencia': <Target className="w-6 h-6 text-green-500" />,
  'Formación': <Book className="w-6 h-6 text-purple-500" />,
};

// Función para determinar el caso de distribución según el número de experiencias
const determineDistributionCase = (experiencesCount: number) => {
  switch (experiencesCount) {
    case 1:
      return DISTRIBUTION_CASES.CASE_1;
    case 2:
      return DISTRIBUTION_CASES.CASE_2;
    case 3:
      return DISTRIBUTION_CASES.CASE_3;
    case 4:
      return DISTRIBUTION_CASES.CASE_4;
    case 5:
      return DISTRIBUTION_CASES.CASE_5;
    case 6:
      return DISTRIBUTION_CASES.CASE_6;
    case 7:
      return DISTRIBUTION_CASES.CASE_7;
    case 8:
    default:
      return DISTRIBUTION_CASES.CASE_8;
  }
};

// Función para distribuir las experiencias en páginas según el caso
const distributeExperiencesByCase = (experiences: any[], currentCase: string, hasCourses: boolean) => {
  // Si no hay experiencias, devolver estructura básica
  if (!experiences || experiences.length === 0) {
    return hasCourses ? [[]] : [];
  }

  // Ordenamos las experiencias por fecha (más reciente primero)
  const sortedExperiences = [...experiences];

  // Caso especial para CV de odontólogo con 4 experiencias pero tratado como caso 5
  const isOdontologoCV = experiences.length === 4 &&
    currentCase === DISTRIBUTION_CASES.CASE_5 &&
    sortedExperiences.some(exp =>
    (exp.title?.toLowerCase().includes('odontólog') ||
      exp.company?.toLowerCase().includes('dental') ||
      exp.company?.toLowerCase().includes('clínica') ||
      exp.title?.toLowerCase().includes('dental')));

  console.log(`¿Es CV especial de odontólogo? ${isOdontologoCV}`);

  // Configuración para cada caso
  switch (currentCase) {
    case DISTRIBUTION_CASES.CASE_1:
      // Todo en una página
      return [sortedExperiences];

    case DISTRIBUTION_CASES.CASE_2:
      // Primera página: 2 experiencias, Segunda página: solo cursos (sin experiencias)
      return [sortedExperiences, []];

    case DISTRIBUTION_CASES.CASE_3:
      // Primera página: 2 experiencias, Segunda página: 1 experiencia + cursos
      return [
        sortedExperiences.slice(0, 2),
        sortedExperiences.slice(2)
      ];

    case DISTRIBUTION_CASES.CASE_4:
      // Primera página: 2 experiencias, Segunda página: 2 experiencias + cursos
      return [
        sortedExperiences.slice(0, 2),
        sortedExperiences.slice(2)
      ];

    case DISTRIBUTION_CASES.CASE_5:
      // Primera página: 2 experiencias, Segunda página: 3 experiencias, Tercera página: cursos
      console.log("Caso 5 - Total de experiencias:", sortedExperiences.length);

      // Si es el CV especial de odontólogo con solo 4 experiencias
      if (isOdontologoCV) {
        console.log("Aplicando distribución especial para CV de odontólogo con 4 experiencias");
        // Dividimos 2 experiencias en la primera página y 2 en la segunda
        return [
          sortedExperiences.slice(0, 2),  // 2 primeras experiencias
          sortedExperiences.slice(2, 4),  // 2 últimas experiencias
          []  // página vacía para cursos
        ];
      }

      console.log("Caso 5 - Experiencias para segunda página:", sortedExperiences.slice(2).length);
      return [
        sortedExperiences.slice(0, 2),  // 2 primeras experiencias
        sortedExperiences.slice(2),     // Todas las experiencias restantes (3, 4 y 5)
        []  // página vacía para cursos
      ];

    case DISTRIBUTION_CASES.CASE_6:
      // Primera página: 2 experiencias, Segunda página: 3 experiencias, Tercera página: 1 experiencia + cursos
      return [
        sortedExperiences.slice(0, 2),
        sortedExperiences.slice(2, 5),
        sortedExperiences.slice(5)
      ];

    case DISTRIBUTION_CASES.CASE_7:
      // Primera página: 2 experiencias, Segunda página: 3 experiencias, Tercera página: 2 experiencias + cursos
      return [
        sortedExperiences.slice(0, 2),
        sortedExperiences.slice(2, 5),
        sortedExperiences.slice(5)
      ];

    case DISTRIBUTION_CASES.CASE_8:
      // Primera página: 2 experiencias, Segunda página: 3 experiencias, Tercera página: 3 experiencias, Cuarta página: cursos
      return [
        sortedExperiences.slice(0, 2),  // 2 primeras experiencias
        sortedExperiences.slice(2, 5),  // experiencias 3, 4 y 5
        sortedExperiences.slice(5, 8),  // experiencias 6, 7 y 8
        []  // página vacía para cursos
      ];

    default:
      // Para cualquier otro caso no previsto, usar una distribución similar al caso 8
      return [
        sortedExperiences.slice(0, 2),
        sortedExperiences.slice(2, 5),
        sortedExperiences.slice(5, 8),
        []
      ];
  }
};

// Componente de Header Colapsable con estilo futurista
interface CollapsibleSectionHeaderProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  actionButtons?: React.ReactNode;
  gradientFrom?: string;
  gradientTo?: string;
}

const CollapsibleSectionHeader: React.FC<CollapsibleSectionHeaderProps> = ({
  title,
  subtitle,
  icon,
  isExpanded,
  onToggle,
  actionButtons,
  gradientFrom = 'from-blue-600',
  gradientTo = 'to-purple-700'
}) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
      {/* Grupo: Icono + Título + Toggle */}
      <div
        className="flex items-center gap-3 cursor-pointer group flex-1"
        onClick={onToggle}
      >
        {/* Icono con efecto hover */}
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradientFrom} ${gradientTo} flex items-center justify-center shadow-[0_0_10px_rgba(138,75,175,0.3)] group-hover:shadow-[0_0_15px_rgba(138,75,175,0.5)] transition-all duration-300`}>
          {icon}
        </div>

        {/* Título y subtítulo */}
        <div className="flex-1">
          <h2 className="text-lg md:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200 group-hover:from-blue-100 group-hover:to-purple-100 transition-all">
            {title}
          </h2>
          <p className="text-purple-100/70 text-sm mt-1">
            {subtitle}
          </p>
        </div>

        {/* Botón de Toggle con animación */}
        <button
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-300 group-hover:scale-110"
          aria-label={isExpanded ? 'Colapsar sección' : 'Expandir sección'}
        >
          <ChevronDown
            className={`w-5 h-5 text-purple-200 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Botones de acción (solo visible si está expandido) */}
      {actionButtons && isExpanded && (
        <div className="flex gap-3 w-full sm:w-auto animate-fadeIn">
          {actionButtons}
        </div>
      )}
    </div>
  );
};

export default function Preview() {
  const navigate = useNavigate();
  const [cvData, setCvData] = useState<{ cv: CVData; coverLetter: string; analysis: any } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(false);
  const [showCoverLetter, setShowCoverLetter] = useState(false);

  // Estados para controlar expansión de secciones (acordeón) - Iniciadas expandidas
  const [isCoverLetterExpanded, setIsCoverLetterExpanded] = useState(true);
  const [isAnalysisSectionExpanded, setIsAnalysisSectionExpanded] = useState(true);
  const [isAssistantExpanded, setIsAssistantExpanded] = useState(true);

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [distributionCase, setDistributionCase] = useState<string | null>(null);
  const firstPageScrollRef = useRef<HTMLDivElement>(null);

  // Estados para manejar la carga progresiva
  const [isCoverLetterLoading, setIsCoverLetterLoading] = useState(false);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);

  // Estados para personalización de fuente
  const [selectedFont, setSelectedFont] = useState('Arial');
  const [fontSize, setFontSize] = useState(14);

  // Estados para el Asistente de Postulación
  const [user, setUser] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ role: string, content: string }[]>([]);
  const [message, setMessage] = useState('');

  // --- NUEVO ESTADO PARA EL MODAL ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Tipo para identificar el contenido: cv-page-1, cv-page-2, ..., letter, analysis
  const [modalContentId, setModalContentId] = useState<string | null>(null);
  // --- FIN NUEVO ESTADO ---

  // --- ESTADO PARA ZOOM EN MODAL ---
  const [modalZoom, setModalZoom] = useState(0.6); // Empezar con zoom 60%
  const MIN_ZOOM = 0.3;
  const MAX_ZOOM = 1.5;
  const ZOOM_STEP = 0.1;
  // --- FIN ESTADO ZOOM --- 

  // --- ESTADOS PARA EDICIÓN DEL CV ---
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedCvData, setEditedCvData] = useState<CVData | null>(null);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [currentEditPage, setCurrentEditPage] = useState(1);
  const [showPreview, setShowPreview] = useState(false); // Estado para alternar vista en móvil
  const [editModalZoom, setEditModalZoom] = useState(0.5); // Zoom específico para modal de edición móvil
  const [showInstructions, setShowInstructions] = useState(false); // Popup de instrucciones

  // --- NUEVOS ESTADOS PARA EDICIÓN DE CARTA ---
  const [isEditLetterModalOpen, setIsEditLetterModalOpen] = useState(false);
  const [editedCoverLetter, setEditedCoverLetter] = useState<string>('');
  const [isLetterUnsavedChanges, setIsLetterUnsavedChanges] = useState(false);
  const [showLetterPreview, setShowLetterPreview] = useState(false); // Estado para alternar vista en móvil

  // Estados para datos personales de la carta
  const [editedName, setEditedName] = useState<string>('');
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [editedLocation, setEditedLocation] = useState<string>('');
  const [editedPhone, setEditedPhone] = useState<string>('');
  const [editedEmail, setEditedEmail] = useState<string>('');
  // --- FIN ESTADOS EDICIÓN ---

  // --- ESTADOS PARA EL CROPPER DE IMAGEN ---
  const [showCropper, setShowCropper] = useState(false);
  const [tempImage, setTempImage] = useState<string>('');
  // --- FIN ESTADOS CROPPER ---

  // Ajustar para asegurar que se muestren más empleos
  const EXPERIENCE_ITEMS_ON_FIRST_PAGE = 4; // Aumentamos a 4 experiencias en la primera página

  // Scroll inmediato al inicio cuando el componente se monta
  useEffect(() => {
    // Scroll inmediato sin animación
    window.scrollTo(0, 0);
    document.body.scrollTop = 0; // Para Safari
    document.documentElement.scrollTop = 0; // Para Chrome, Firefox, IE y Opera
  }, []);

  useEffect(() => {
    const data = localStorage.getItem('cvData');
    if (!data) {
      navigate('/dashboard');
      return;
    }
    try {
      const parsedData = JSON.parse(data);
      console.log("CV Data cargada:", parsedData);
      console.log("Template recibido:", parsedData.cv.template);
      console.log("Color del template recibido:", parsedData.cv.templateColor);

      // Verificar si hay partes del CV que aún se están generando
      if (!parsedData.coverLetter) {
        setIsCoverLetterLoading(true);
      }

      if (!parsedData.analysis) {
        setIsAnalysisLoading(true);
      }

      // Cargar configuraciones de fuente si existen
      if (parsedData.cv.fontFamily) {
        const fontValue = FONT_OPTIONS.find(f => f.style === parsedData.cv.fontFamily)?.value || 'Arial';
        setSelectedFont(fontValue);
      }
      if (parsedData.cv.fontSize) {
        setFontSize(parsedData.cv.fontSize);
      }

      // Comprobamos si hay experiencia de internado o práctica que deba incluirse
      let experiences = parsedData.cv.experience || [];
      console.log("Experiencias laborales originales:", experiences.length, experiences);

      // Verificación especial para el caso del CV con 5 experiencias
      // Buscar palabras clave en los títulos o descripciones que puedan indicar experiencias adicionales
      const originalTitle = parsedData.cv.title || '';
      const experienceTitles = experiences.map((exp: any) => exp.title?.toLowerCase() || '');
      const experienceCompanies = experiences.map((exp: any) => exp.company?.toLowerCase() || '');

      const isOdontologoCV = originalTitle.toLowerCase().includes('odontólogo') ||
        originalTitle.toLowerCase().includes('odontolog') ||
        experienceTitles.some((title: string) =>
          title.includes('odontólogo') || title.includes('dental'));

      // Si es el CV del odontólogo con 4 experiencias pero sabemos que deben ser 5
      if (isOdontologoCV && experiences.length === 4) {
        console.log("CV de Odontólogo detectado con 4 experiencias. Forzando Caso 5.");
        setDistributionCase(DISTRIBUTION_CASES.CASE_5);
      } else {
        // Determinar el caso de distribución normal
        const experiencesCount = experiences.length;
        const currentCase = determineDistributionCase(experiencesCount);
        setDistributionCase(currentCase);
        console.log("Caso de distribución:", currentCase);
      }

      console.log("Experiencias laborales:", parsedData.cv.experience);
      console.log("Número total de experiencias:", parsedData.cv.experience.length);

      setCvData(parsedData);

      // Asegurar que la página se desplace al inicio cuando se carga el CV
      window.scrollTo({ top: 0, behavior: 'auto' }); // Cambiado a 'auto' para un scroll inmediato

      // También podemos forzar el scroll al principio del contenedor de vista previa
      if (previewContainerRef.current) {
        previewContainerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    } catch (error) {
      console.error('Error parsing CV data:', error);
      navigate('/dashboard');
    }
  }, [navigate]);

  // --- Reintroducir useEffect para scroll inicial ---
  useEffect(() => {
    // Usamos setTimeout para asegurar que el DOM esté listo y las dimensiones calculadas
    const timer = setTimeout(() => {
      const initialScrollOffset = 0; // Cambiado de 150 a 0 para empezar desde la izquierda
      if (firstPageScrollRef.current) {
        firstPageScrollRef.current.scrollLeft = initialScrollOffset;
        console.log(`Preview.tsx: Set initial scrollLeft for first page wrapper to ${initialScrollOffset}`);

        // Aplicar el mismo scroll a las páginas adicionales
        const additionalWrappers = document.querySelectorAll('.additional-page-scroll-wrapper');
        additionalWrappers.forEach(wrapper => {
          if (wrapper instanceof HTMLElement) {
            wrapper.scrollLeft = initialScrollOffset;
          }
        });
        console.log(`Preview.tsx: Attempted to set initial scrollLeft for ${additionalWrappers.length} additional pages.`);
      }
    }, 0);

    return () => clearTimeout(timer); // Limpiar el timeout
  }, [cvData]); // Re-ejecutar si cvData cambia
  // --- FIN useEffect scroll inicial ---

  // Escuchar eventos de actualización para componentes adicionales
  useEffect(() => {
    // Función para actualizar la carta de presentación cuando esté lista
    const handleCoverLetterUpdate = () => {
      console.log("🔄 Evento: Carta de presentación generada");
      const data = localStorage.getItem('cvData');
      if (data) {
        try {
          const updatedData = JSON.parse(data);
          if (updatedData.coverLetter) {
            setCvData(updatedData);
            setIsCoverLetterLoading(false);
          }
        } catch (error) {
          console.error('Error updating cover letter:', error);
        }
      }
    };

    // Función para actualizar el análisis cuando esté listo
    const handleAnalysisUpdate = () => {
      console.log("🔄 Evento: Análisis generado");
      const data = localStorage.getItem('cvData');
      if (data) {
        try {
          const updatedData = JSON.parse(data);
          if (updatedData.analysis) {
            setCvData(updatedData);
            setIsAnalysisLoading(false);
          }
        } catch (error) {
          console.error('Error updating analysis:', error);
        }
      }
    };

    // Escuchar los eventos personalizados
    window.addEventListener('coverLetterGenerated', handleCoverLetterUpdate);
    window.addEventListener('analysisGenerated', handleAnalysisUpdate);

    // Limpiar los listeners cuando se desmonte el componente
    return () => {
      window.removeEventListener('coverLetterGenerated', handleCoverLetterUpdate);
      window.removeEventListener('analysisGenerated', handleAnalysisUpdate);
    };
  }, []);

  const handleExport = async (type: 'pdf' | 'images' | 'analysisPDF' | 'coverLetterPDF' | 'coverLetterImages' | 'analysisImages') => {
    if (!cvData?.cv) return;

    setIsExporting(true);
    try {
      const filename = `cv-${cvData.cv.name.toLowerCase().replace(/\s+/g, '-')}`;

      switch (type) {
        case 'coverLetterPDF':
          await exportServices.toPDF('cover-letter-preview', `carta-${filename}.pdf`);
          break;
        case 'analysisPDF':
          await exportServices.toPDF('analysis-section', `analisis-${filename}.pdf`);
          break;
        case 'pdf':
          await exportServices.toPDF('cv-preview', `${filename}.pdf`);
          break;
        case 'images':
          await exportServices.toImages('cv-preview');
          break;
        case 'coverLetterImages':
          await exportServices.toImages('cover-letter-preview');
          break;
        case 'analysisImages':
          await exportServices.toImages('analysis-section');
          break;
      }
    } catch (error) {
      console.error(`Error exporting to ${type}:`, error);
    } finally {
      setIsExporting(false);
    }
  };

  const renderStars = (score: number) => {
    const stars = [];
    const fullStars = Math.floor(score / 2);
    const hasHalfStar = score % 2 >= 1;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<StarHalf key={i} className="w-5 h-5 text-yellow-400 fill-current" />);
      } else {
        stars.push(<Star key={i} className="w-5 h-5 text-gray-400" />);
      }
    }
    return stars;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const renderCoverLetter = () => {
    if (!cvData?.cv || !cvData.coverLetter) return null;

    const currentDate = new Date().toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    // Parseamos la carta que ahora viene en formato JSON
    const letterContent = typeof cvData.coverLetter === 'string'
      ? JSON.parse(cvData.coverLetter)
      : cvData.coverLetter;

    return (
      <div className="relative">
        {/* Efectos decorativos - Comentados temporalmente para prueba de exportación PDF */}
        {/* 
        <div className="absolute -left-2 -top-2 w-16 h-16 bg-blue-500/10 rounded-full blur-lg"></div>
        <div className="absolute -right-2 -bottom-2 w-16 h-16 bg-purple-500/10 rounded-full blur-lg"></div>
        */}

        {/* Encabezado */}
        <div className="border-b border-gray-200/30 pb-6 mb-10 relative">
          {/* Comentado temporalmente: Línea gradiente bajo encabezado */}
          {/* <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div> */}
          <div className="flex justify-between items-start">
            <div className="space-y-3">
              {/* Nombre con estilo mejorado para PDF */}
              <h1
                className="text-2xl font-bold text-purple-700"
                style={{ color: '#6d28d9', fontWeight: 'bold', fontFamily: 'Arial, sans-serif' }}
              >
                {cvData.cv.name}
              </h1>
              <p
                className="text-gray-700 font-medium"
                style={{ color: '#4a5568', fontWeight: 500 }}
              >
                {cvData.cv.title}
              </p>
              <div className="text-sm text-gray-600/80 space-y-1.5">
                <p className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                  </span>
                  {cvData.cv.contact.location}
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                  </span>
                  {cvData.cv.contact.phone}
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                  </span>
                  {cvData.cv.contact.email}
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-100/90 to-purple-100/90 p-2 rounded-lg shadow-inner">
              <p className="text-gray-600 text-right font-medium">{currentDate}</p>
            </div>
          </div>
        </div>

        {/* Contenido de la carta */}
        <div className="space-y-6 text-gray-700 leading-relaxed">
          <p className="text-gray-800 font-medium text-lg relative inline-block">
            Estimado/a Equipo de Recursos Humanos:
            {/* Comentado temporalmente: Subrayado gradiente */}
            {/* <span className="absolute -bottom-1 left-0 h-0.5 w-1/3 bg-gradient-to-r from-purple-500/50 to-transparent rounded-full"></span> */}
          </p>

          <div className="space-y-5 text-gray-700 leading-relaxed">
            {letterContent.paragraphs.map((paragraph: string, index: number) => (
              <p key={index} className="text-justify">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Firma */}
          <div className="mt-12 pt-6 border-t border-gray-200/30 relative">
            {/* Comentado temporalmente: Línea gradiente sobre firma */}
            {/* <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div> */}
            <p className="text-gray-800 font-medium">Atentamente,</p>
            <div className="mt-5">
              <div className="inline-block relative bg-purple-50/50 px-4 py-2 rounded">
                <p
                  className="font-bold text-purple-700"
                  style={{ color: '#6d28d9', fontWeight: 'bold', fontFamily: 'Arial, sans-serif' }}
                >
                  {cvData.cv.name}
                </p>
                <p
                  className="text-gray-700"
                  style={{ color: '#4a5568' }}
                >
                  {cvData.cv.title}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // --- NUEVA FUNCION PARA ABRIR MODAL ---
  const openModal = (contentId: string) => {
    setModalContentId(contentId);
    setIsModalOpen(true);
    // Opcional: Deshabilitar scroll del body mientras el modal está abierto
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContentId(null);
    // Opcional: Habilitar scroll del body de nuevo
    document.body.style.overflow = 'auto';
  };
  // --- FIN NUEVA FUNCION ---

  // --- FUNCIONES ZOOM MODAL ---
  const handleZoomIn = () => {
    setModalZoom(prevZoom => Math.min(prevZoom + ZOOM_STEP, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setModalZoom(prevZoom => Math.max(prevZoom - ZOOM_STEP, MIN_ZOOM));
  };
  // --- FIN FUNCIONES ZOOM ---

  // Función simple para editar CV
  const handleEditCV = () => {
    alert('¡Función de edición activada! Próximamente podrás editar tu CV directamente desde aquí. Por ahora, puedes generar un nuevo CV desde el Dashboard si necesitas hacer cambios.');
  };

  // Funciones completas para edición del CV
  const openEditModal = () => {
    if (cvData?.cv) {
      console.log('Debug - CV data completa:', cvData.cv);
      console.log('Debug - Campo about:', cvData.cv.about);
      console.log('Debug - Experiencias:', cvData.cv.experience);
      setEditedCvData({ ...cvData.cv });
      setIsEditModalOpen(true);
      setIsUnsavedChanges(false);
      setCurrentEditPage(1);
      setShowPreview(false); // Empezar siempre en modo edición
      setEditModalZoom(0.5); // Resetear zoom inicial
      document.body.style.overflow = 'hidden';

      // Eliminado: No mostrar instrucciones automáticamente al abrir modal
    }
  };

  const closeEditModal = () => {
    if (isUnsavedChanges) {
      const confirmClose = window.confirm('¿Estás seguro de que quieres cerrar sin guardar los cambios?');
      if (!confirmClose) return;
    }
    setIsEditModalOpen(false);
    setEditedCvData(null);
    setIsUnsavedChanges(false);
    document.body.style.overflow = 'auto';
  };

  const handleSaveChanges = () => {
    if (editedCvData && cvData) {
      // Recalcular el caso de distribución basado en las nuevas experiencias
      const newExperiencesCount = editedCvData.experience?.length || 0;
      const newDistributionCase = determineDistributionCase(newExperiencesCount);

      // Actualizar el estado de distribución
      setDistributionCase(newDistributionCase);

      // Guardar los datos actualizados
      const updatedCvData = { ...cvData, cv: editedCvData };
      localStorage.setItem('cvData', JSON.stringify(updatedCvData));
      setCvData(updatedCvData);
      setIsUnsavedChanges(false);
      setIsEditModalOpen(false);
      document.body.style.overflow = 'auto';

      console.log(`Cambios guardados. Nuevo caso de distribución: ${newDistributionCase}, Experiencias: ${newExperiencesCount}`);
      alert('¡Cambios guardados exitosamente!');
    }
  };

  // --- NUEVAS FUNCIONES PARA EDICIÓN DE CARTA ---
  const openEditLetterModal = () => {
    if (cvData?.coverLetter) {
      // Si la carta es un string JSON, parsearlo; si no, usarlo como está
      let letterContent = '';
      try {
        const parsed = typeof cvData.coverLetter === 'string'
          ? JSON.parse(cvData.coverLetter)
          : cvData.coverLetter;

        if (parsed.paragraphs && Array.isArray(parsed.paragraphs)) {
          letterContent = parsed.paragraphs.join('\n\n');
        } else {
          letterContent = cvData.coverLetter;
        }
      } catch {
        letterContent = cvData.coverLetter;
      }

      // Cargar contenido de la carta
      setEditedCoverLetter(letterContent);

      // Cargar datos personales
      setEditedName(cvData.cv?.name || '');
      setEditedTitle(cvData.cv?.title || '');
      setEditedLocation(cvData.cv?.contact?.location || '');
      setEditedPhone(cvData.cv?.contact?.phone || '');
      setEditedEmail(cvData.cv?.contact?.email || '');

      setIsEditLetterModalOpen(true);
      setIsLetterUnsavedChanges(false);
      setShowLetterPreview(false); // Empezar siempre en modo edición
      document.body.style.overflow = 'hidden';
    }
  };

  const closeEditLetterModal = () => {
    if (isLetterUnsavedChanges) {
      const confirmClose = window.confirm('¿Estás seguro de que quieres cerrar sin guardar los cambios?');
      if (!confirmClose) return;
    }
    setIsEditLetterModalOpen(false);
    setEditedCoverLetter('');
    setIsLetterUnsavedChanges(false);
    setShowLetterPreview(false); // Resetear vista previa
    document.body.style.overflow = 'auto';
  };

  const handleSaveLetterChanges = () => {
    if (editedCoverLetter && cvData) {
      // Convertir el texto editado de vuelta al formato JSON esperado
      const paragraphs = editedCoverLetter.split('\n\n').filter(p => p.trim() !== '');
      const letterData = JSON.stringify({ paragraphs });

      // Actualizar también los datos personales del CV
      const updatedCvData = {
        ...cvData,
        coverLetter: letterData,
        cv: {
          ...cvData.cv,
          name: editedName,
          title: editedTitle,
          contact: {
            ...cvData.cv.contact,
            location: editedLocation,
            phone: editedPhone,
            email: editedEmail
          }
        }
      };

      localStorage.setItem('cvData', JSON.stringify(updatedCvData));
      setCvData(updatedCvData);
      setIsLetterUnsavedChanges(false);
      setIsEditLetterModalOpen(false);
      setShowLetterPreview(false); // Resetear vista previa
      document.body.style.overflow = 'auto';
      alert('¡Carta de presentación y datos personales guardados exitosamente!');
    }
  };

  const updateCoverLetter = (value: string) => {
    setEditedCoverLetter(value);
    setIsLetterUnsavedChanges(true);
  };

  // Funciones para actualizar datos personales
  const updatePersonalData = (field: string, value: string) => {
    switch (field) {
      case 'name':
        setEditedName(value);
        break;
      case 'title':
        setEditedTitle(value);
        break;
      case 'location':
        setEditedLocation(value);
        break;
      case 'phone':
        setEditedPhone(value);
        break;
      case 'email':
        setEditedEmail(value);
        break;
    }
    setIsLetterUnsavedChanges(true);
  };

  // Función para eliminar experiencia laboral
  const deleteExperience = (experienceIndex: number) => {
    if (!editedCvData) return;

    const confirmDelete = window.confirm(
      `¿Estás seguro de que quieres eliminar la experiencia "${editedCvData.experience[experienceIndex]?.title || 'Sin título'}"? Esta acción no se puede deshacer.`
    );

    if (confirmDelete) {
      const newExperiences = [...(editedCvData.experience || [])];
      newExperiences.splice(experienceIndex, 1);

      const newData = { ...editedCvData, experience: newExperiences };
      setEditedCvData(newData);
      setIsUnsavedChanges(true);

      // Mostrar notificación de éxito
      alert('Experiencia eliminada exitosamente');
    }
  };

  // Función para eliminar curso
  const deleteCourse = (courseIndex: number) => {
    if (!editedCvData) return;

    const confirmDelete = window.confirm(
      `¿Estás seguro de que quieres eliminar el curso "${editedCvData.courses[courseIndex]?.title || 'Sin título'}"? Esta acción no se puede deshacer.`
    );

    if (confirmDelete) {
      const newCourses = [...(editedCvData.courses || [])];
      newCourses.splice(courseIndex, 1);

      const newData = { ...editedCvData, courses: newCourses };
      setEditedCvData(newData);
      setIsUnsavedChanges(true);

      // Mostrar notificación de éxito
      alert('Curso eliminado exitosamente');
    }
  };

  // Función para aplicar configuraciones de fuente al CV
  const applyFontSettings = () => {
    const selectedFontStyle = FONT_OPTIONS.find(font => font.value === selectedFont);
    if (selectedFontStyle && editedCvData) {
      const updatedData = {
        ...editedCvData,
        fontFamily: selectedFontStyle.style,
        fontSize: fontSize
      };
      setEditedCvData(updatedData);
      setIsUnsavedChanges(true);
    }
  };

  // Aplicar configuraciones automáticamente cuando cambian
  useEffect(() => {
    if (editedCvData) {
      applyFontSettings();
    }
  }, [selectedFont, fontSize]);

  // Forzar re-renderización cuando cambie el caso de distribución
  useEffect(() => {
    if (distributionCase && cvData) {
      console.log(`Caso de distribución actualizado: ${distributionCase}`);
      // Forzar re-cálculo de páginas
      const newPages = distributeExperiencesByCase(
        cvData.cv.experience || [],
        distributionCase,
        (cvData.cv.courses && cvData.cv.courses.length > 0)
      );
      console.log(`Páginas recalculadas: ${newPages.length}`);
    }
  }, [distributionCase, cvData]);
  // --- FIN FUNCIONES CARTA ---

  // Funciones para controlar zoom del modal de edición
  const handleEditModalZoomIn = () => {
    setEditModalZoom(prev => Math.min(1.2, prev + 0.1));
  };

  const handleEditModalZoomOut = () => {
    setEditModalZoom(prev => Math.max(0.3, prev - 0.1));
  };

  const handleEditModalZoomReset = () => {
    setEditModalZoom(0.5);
  };

  // Función para mostrar popup de instrucciones - ELIMINADA
  // const showInstructionsPopup = () => {
  //   setShowInstructions(true);
  //   setTimeout(() => {
  //     setShowInstructions(false);
  //   }, 3000);
  // };

  const updateEditedData = (field: string, value: any, experienceIndex?: number) => {
    if (!editedCvData) return;

    const newData = { ...editedCvData };

    if (field.startsWith('experience.') && experienceIndex !== undefined) {
      // Asegurar que el array de experience existe y tiene el índice necesario
      if (!newData.experience) newData.experience = [];
      if (!newData.experience[experienceIndex]) {
        console.error(`Experience index ${experienceIndex} doesn't exist`);
        return;
      }

      if (field.includes('.achievements.')) {
        // Manejar achievements individuales: experience.achievements.0
        const achIndex = parseInt(field.split('.achievements.')[1]);
        if (!newData.experience[experienceIndex].achievements) {
          newData.experience[experienceIndex].achievements = [];
        }
        newData.experience[experienceIndex].achievements[achIndex] = value;
      } else if (field === 'experience.achievements') {
        // Manejar el array completo de achievements
        newData.experience[experienceIndex].achievements = value;
      } else {
        const experienceField = field.replace('experience.', '');
        (newData.experience[experienceIndex] as any)[experienceField] = value;
      }
    } else if (field.startsWith('education.') && experienceIndex !== undefined) {
      const educationField = field.replace('education.', '');
      if (!newData.education) newData.education = [];
      if (!newData.education[experienceIndex]) {
        newData.education[experienceIndex] = { degree: '', institution: '', period: '' };
      }
      (newData.education[experienceIndex] as any)[educationField] = value;
    } else if (field.startsWith('courses.') && experienceIndex !== undefined) {
      const courseField = field.replace('courses.', '');
      if (!newData.courses) newData.courses = [];
      if (!newData.courses[experienceIndex]) {
        newData.courses[experienceIndex] = { title: '', institution: '', period: '' };
      }
      (newData.courses[experienceIndex] as any)[courseField] = value;
    } else if (field.startsWith('contact.')) {
      const contactField = field.replace('contact.', '');
      if (!newData.contact) {
        newData.contact = { email: '', phone: '', location: '' };
      }
      (newData.contact as any)[contactField] = value;
    } else {
      (newData as any)[field] = value;
    }

    setEditedCvData(newData);
    setIsUnsavedChanges(true);
  };

  // --- FUNCIONES PARA EL CROPPER DE IMAGEN ---
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen válida');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === 'string') {
        setTempImage(e.target.result);
        setShowCropper(true);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedImage: string) => {
    updateEditedData('photo', croppedImage);
    setShowCropper(false);
    setTempImage('');
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setTempImage('');
  };
  // --- FIN FUNCIONES CROPPER ---

  // --- FUNCIÓN PARA ELIMINAR PÁGINAS COMPLETAS ---
  const deletePage = (pageNumber: number) => {
    if (!editedCvData || !finalPages) return;

    // Verificar si es la única página
    if (finalPages.length <= 1) {
      alert('No puedes eliminar la única página del CV. Debe tener al menos una página.');
      return;
    }

    // Mostrar información específica sobre qué se eliminará
    let contentToDelete = '';
    if (pageNumber === 1) {
      contentToDelete = 'información personal, contacto y las experiencias de la primera página';
    } else if (pageNumber === 2) {
      contentToDelete = 'las experiencias de la segunda página';
    } else if (pageNumber === 3) {
      contentToDelete = 'las experiencias de la tercera página y los cursos (si los hay)';
    } else {
      contentToDelete = 'el contenido de esta página';
    }

    const confirmDelete = window.confirm(
      `¿Estás seguro de que quieres eliminar completamente la página ${pageNumber}?\n\nSe eliminará: ${contentToDelete}\n\nLas páginas restantes se reorganizarán automáticamente.\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmDelete) return;

    const newData = { ...editedCvData };
    const currentExperiences = newData.experience || [];
    const currentCourses = newData.courses || [];

    // Obtener las experiencias que están en la página a eliminar
    const pageExperiences = finalPages[pageNumber - 1] || [];

    // Eliminar las experiencias de esa página específica
    let newExperiences = [...currentExperiences];

    // Encontrar y eliminar las experiencias de la página específica
    pageExperiences.forEach(expToDelete => {
      const indexToDelete = newExperiences.findIndex(exp =>
        exp.title === expToDelete.title &&
        exp.company === expToDelete.company &&
        exp.startDate === expToDelete.startDate
      );
      if (indexToDelete !== -1) {
        newExperiences.splice(indexToDelete, 1);
      }
    });

    // Si es la página 1, también eliminar información personal
    if (pageNumber === 1) {
      newData.name = '';
      newData.title = '';
      newData.email = '';
      newData.phone = '';
      newData.location = '';
      newData.about = '';
      newData.education = [];
      newData.photo = null;
    }

    // Si eliminamos una página que contiene cursos, eliminar los cursos
    const shouldDeleteCourses = (
      (pageNumber === finalPages.length && currentCourses.length > 0) || // Última página con cursos
      (pageNumber === 3 && finalPages.length === 3 && currentCourses.length > 0) || // Página 3 en CV de 3 páginas
      (pageNumber === 2 && finalPages.length === 2 && currentCourses.length > 0) // Página 2 en CV de 2 páginas
    );

    if (shouldDeleteCourses) {
      newData.courses = [];
    }

    // Actualizar las experiencias
    newData.experience = newExperiences;

    setEditedCvData(newData);
    setIsUnsavedChanges(true);

    // Recalcular páginas después de la eliminación
    const newPages = distributeExperiencesByCase(
      newExperiences,
      determineDistributionCase(newExperiences.length),
      (newData.courses && newData.courses.length > 0)
    );

    // Actualizar el caso de distribución
    setDistributionCase(determineDistributionCase(newExperiences.length));

    // Navegar a una página válida
    let newCurrentPage = currentEditPage;

    if (pageNumber <= currentEditPage) {
      // Si eliminamos la página actual o una anterior, ir a la página anterior
      newCurrentPage = Math.max(1, currentEditPage - 1);
    }

    // Asegurar que no excedamos el número de páginas disponibles
    newCurrentPage = Math.min(newCurrentPage, newPages.length);

    setCurrentEditPage(newCurrentPage);

    // Mostrar mensaje de éxito
    setTimeout(() => {
      alert(`Página ${pageNumber} eliminada completamente.\n\nEl CV ahora tiene ${newPages.length} página${newPages.length !== 1 ? 's' : ''}.\n\nNavegando a página ${newCurrentPage}.`);
    }, 100);
  };
  // --- FIN FUNCIÓN ELIMINAR PÁGINAS ---

  // Obtener el usuario para el avatar en el chat
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
      }
    };
    getUser();
  }, []);

  // Añadir useEffect para manejar los eventos del chat
  useEffect(() => {
    // Asegurarse de que se ejecute solo en el cliente
    if (typeof window === 'undefined') return;

    const sendButton = document.getElementById('send-message');
    const userMessageInput = document.getElementById('user-message') as HTMLTextAreaElement;
    const messagesContainer = document.getElementById('chat-messages');

    const handleSendMessage = async () => {
      if (!userMessageInput || !messagesContainer || !userMessageInput.value.trim()) return;

      const userMessage = userMessageInput.value.trim();
      userMessageInput.value = '';

      // Añadir mensaje del usuario
      const userMessageHTML = `
      <div class="flex items-start gap-3 justify-end animate-fade-in">
        <div class="bg-purple-600/30 backdrop-blur-md rounded-2xl rounded-tr-none px-4 py-3 max-w-[80%] sm:max-w-[85%] md:max-w-[90%] shadow-lg border border-purple-500/20">
            <p class="text-purple-50">${userMessage}</p>
        </div>
        <div class="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-md flex-shrink-0 overflow-hidden">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-white"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
        </div>
      </div>
    `;

      messagesContainer.innerHTML += userMessageHTML;
      messagesContainer.scrollTop = messagesContainer.scrollHeight;

      // Mostrar indicador de escritura
      const typingIndicatorHTML = `
      <div id="typing-indicator" class="flex items-start gap-3 animate-fade-in">
        <div class="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-md flex-shrink-0 overflow-hidden border border-purple-200/20">
          <img src="/img/icono_curriculab.png" alt="Asistente" class="w-full h-full object-cover" />
        </div>
        <div class="bg-indigo-900/40 backdrop-blur-md rounded-2xl rounded-tl-none px-4 py-3 max-w-[80%] sm:max-w-[70%] shadow-lg border border-indigo-500/20">
          <div class="flex space-x-2">
            <div class="w-2 h-2 rounded-full bg-purple-300 animate-bounce"></div>
            <div class="w-2 h-2 rounded-full bg-purple-300 animate-bounce" style="animation-delay: 0.2s"></div>
            <div class="w-2 h-2 rounded-full bg-purple-300 animate-bounce" style="animation-delay: 0.4s"></div>
          </div>
        </div>
      </div>
    `;

      messagesContainer.innerHTML += typingIndicatorHTML;
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      setIsProcessing(true);

      try {
        // Obtener respuesta real del asistente
        const response = await getChatbotResponse(userMessage, cvData?.cv || {});

        // Quitar indicador de escritura
        const typingIndicator = document.getElementById('typing-indicator');
        if (typingIndicator) typingIndicator.remove();

        // Dividir la respuesta en comentario y sugerencia
        let botResponse = response;
        let userAnswer = "";

        // Si la respuesta contiene algún formato que indique una sugerencia, intentamos dividirla
        if (response && response.includes("\"") && response.includes("\n")) {
          const parts = response.split("\n").filter(part => part.trim() !== "");
          if (parts.length >= 2) {
            botResponse = parts[0];
            userAnswer = parts.slice(1).join("\n").replace(/^["']|["']$/g, '');
          }
        }

        // Añadir respuesta del asistente
        const botMessageHTML = `
        <div class="flex items-start gap-3 animate-fade-in">
          <div class="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-md flex-shrink-0 overflow-hidden border border-purple-200/20">
            <img src="/img/icono_curriculab.png" alt="Asistente" class="w-full h-full object-cover" />
          </div>
          <div class="flex flex-col gap-2">
            <div class="bg-indigo-900/40 backdrop-blur-md rounded-2xl rounded-tl-none px-4 py-3 max-w-[80%] sm:max-w-[85%] md:max-w-[90%] shadow-lg border border-indigo-500/20 relative">
              <p class="text-gray-100 pr-6">${botResponse || ''}</p>
              <button class="absolute top-3 right-3 text-white/60 hover:text-white/90 transition-colors js-copy-btn" data-text="${(botResponse || '').replace(/"/g, '&quot;')}" title="Copiar texto">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="js-copy-icon"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="js-check-icon hidden"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </button>
            </div>
            ${userAnswer ? `
              <div class="bg-purple-800/30 backdrop-blur-md rounded-2xl px-4 py-3 max-w-[80%] sm:max-w-[85%] md:max-w-[90%] shadow-lg border border-purple-500/20 relative">
                <div class="flex justify-between items-start mb-1">
                  <p class="text-xs text-purple-300">Sugerencia de respuesta:</p>
                  <button class="text-purple-300 hover:text-white p-1 js-copy-btn" data-text="${userAnswer.replace(/"/g, '&quot;')}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="js-copy-icon"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="js-check-icon hidden"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </button>
                </div>
                <p class="text-white leading-relaxed">${userAnswer}</p>
              </div>
            ` : ''}
          </div>
        </div>
      `;

        messagesContainer.innerHTML += botMessageHTML;
        messagesContainer.scrollTop = messagesContainer.scrollHeight;

        // Inicializar los botones de copia
        setTimeout(() => {
          const copyButtons = document.querySelectorAll('.js-copy-btn');
          copyButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
              const target = e.currentTarget as HTMLElement;
              const text = target.getAttribute('data-text') || '';
              copyToClipboard(text);

              // Cambiar icono temporalmente
              const copyIcon = target.querySelector('.js-copy-icon');
              const checkIcon = target.querySelector('.js-check-icon');
              if (copyIcon && checkIcon) {
                copyIcon.classList.add('hidden');
                checkIcon.classList.remove('hidden');

                setTimeout(() => {
                  copyIcon.classList.remove('hidden');
                  checkIcon.classList.add('hidden');
                }, 2000);
              }
            });
          });
        }, 100);
      } catch (error) {
        console.error('Error al obtener respuesta:', error);
        // Quitar indicador de escritura
        const typingIndicator = document.getElementById('typing-indicator');
        if (typingIndicator) typingIndicator.remove();

        // Mostrar mensaje de error
        const errorMessageHTML = `
        <div class="flex items-start gap-3 animate-fade-in">
          <div class="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-md flex-shrink-0 overflow-hidden border border-purple-200/20">
            <img src="/img/icono_curriculab.png" alt="Asistente" class="w-full h-full object-cover" />
          </div>
            <div class="bg-indigo-900/40 backdrop-blur-md rounded-2xl rounded-tl-none px-4 py-3 max-w-[80%] sm:max-w-[70%] shadow-lg border border-indigo-500/20">
              <p class="text-gray-100">Lo siento, ha ocurrido un error al procesar tu pregunta. Por favor, intenta nuevamente.</p>
          </div>
        </div>
      `;

        messagesContainer.innerHTML += errorMessageHTML;
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      } finally {
        setIsProcessing(false);
      }
    };

    // Añadir event listeners
    if (sendButton) {
      sendButton.addEventListener('click', handleSendMessage);
    }

    if (userMessageInput) {
      userMessageInput.addEventListener('keypress', (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSendMessage();
        }
      });
    }

    // Cleanup al desmontar
    return () => {
      if (sendButton) {
        sendButton.removeEventListener('click', handleSendMessage);
      }
      if (userMessageInput) {
        userMessageInput.removeEventListener('keypress', handleSendMessage);
      }
    };
  }, [cvData, setIsProcessing]);

  if (!cvData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const { cv } = cvData;
  const TemplateComponent = templates[cv.template as keyof typeof templates] || templates.template1;
  console.log("Template utilizado:", cv.template);
  console.log("Color personalizado:", cv.templateColor);

  // Verificamos que tenemos experiencias 
  const totalExperiences = cv.experience?.length || 0;
  const currentCase = distributionCase || determineDistributionCase(totalExperiences);
  console.log("Total de experiencias en el CV:", totalExperiences);
  console.log("Caso de distribución aplicado:", currentCase);

  // Verificamos si hay cursos
  const hasCourses = cv.courses && cv.courses.length > 0;
  console.log("¿Hay cursos disponibles?", hasCourses);

  // Aplicamos la distribución según el caso
  const pages = distributeExperiencesByCase(cv.experience, currentCase, hasCourses);
  console.log("Páginas generadas:", pages.length);
  pages.forEach((page, index) => {
    console.log(`Experiencias en página ${index + 1}:`, page.length);
  });

  // Para el caso 5, aseguramos que siempre haya 3 páginas SOLO si realmente hay suficientes experiencias
  if (currentCase === DISTRIBUTION_CASES.CASE_5 && hasCourses && cv.experience.length >= 3) {
    // Verificamos que existan las tres páginas necesarias
    if (pages.length < 3) {
      // Si faltan páginas, añadimos las necesarias
      while (pages.length < 3) {
        pages.push([]);
      }
      console.log("Forzando tercera página para cursos en Caso 5");
      console.log("Nuevas páginas generadas:", pages.length);
    }

    // Aseguramos que la tercera página exista y esté vacía (para que solo tenga cursos)
    if (pages.length >= 3) {
      pages[2] = []; // Esto asegura que la tercera página solo tenga cursos
    }

    // Para el caso especial del CV de odontólogo, aseguramos que la segunda página tenga 3 experiencias
    // incluso si solo hay 4 en total
    if (cv.experience.length === 4 && cv.title && cv.title.toLowerCase().includes('odontólog')) {
      console.log("Caso especial de CV de odontólogo con 4 experiencias detectadas. Distribuyendo como si fueran 5.");
      // Si la segunda página tiene menos de 3 experiencias, agregamos una experiencia 
      // de la primera página si es necesario
      if (pages[1].length < 2) {
        console.log("Ajustando distribución para asegurar 2 experiencias en página 1 y 2 en página 2");
        pages[0] = cv.experience.slice(0, 2);
        pages[1] = cv.experience.slice(2, 4);
      }
    }
  }

  // Reglas especiales para plantillas específicas - SOLO si hay experiencias suficientes
  if (cv.template === 'template7' && hasCourses && cv.experience.length >= 1) {
    // Template7 siempre requiere una página adicional para cursos
    if (pages.length === 1) {
      pages.push([]);
      console.log("Forzando segunda página para cursos en Template7");
    }
  }
  else if (cv.template === 'template6' && hasCourses && cv.experience.length >= 1) {
    // Template6 requiere una página adicional para cursos
    if (pages.length === 1) {
      pages.push([]);
      console.log("Forzando segunda página para cursos en Template6");
    }
  }

  // Filtrar páginas vacías al final si no hay cursos para justificarlas
  const filteredPages = pages.filter((page, index) => {
    // Mantener la primera página siempre
    if (index === 0) return true;

    // Mantener páginas que tienen experiencias
    if (page.length > 0) return true;

    // Mantener páginas vacías solo si hay cursos y es la última página
    if (hasCourses && index === pages.length - 1) return true;

    // Eliminar páginas vacías sin justificación
    return false;
  });

  console.log(`Páginas filtradas: ${filteredPages.length} (de ${pages.length} originales)`);

  // Usar las páginas filtradas
  const finalPages = filteredPages;

  // Primera página
  const firstPageExperiences = finalPages[0] || [];

  // Verificación adicional para debug
  console.log("Primera página experiencias:", firstPageExperiences.length, firstPageExperiences);

  // Si el caso es 1, incluimos cursos en la primera página
  const includeCoursesInFirstPage = currentCase === DISTRIBUTION_CASES.CASE_1 && hasCourses;
  console.log("¿Incluir cursos en primera página?", includeCoursesInFirstPage);

  if (!cv.experience || cv.experience.length === 0) {
    return <div className="text-center py-12 text-gray-400">No hay experiencia para mostrar</div>;
  }

  // Función para copiar texto al portapapeles  
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback para navegadores más antiguos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Error al copiar al portapapeles:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  // Función para abrir chat en pantalla completa (placeholder)
  const openChatFullscreen = () => {
    alert('Función de chat en pantalla completa estará disponible próximamente');
  };

  return (
    <div
      ref={previewContainerRef}
      className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black pt-12 pb-8 px-2 sm:px-4"
      id="preview-container-top"
    >
      {/* --- ESTRUCTURA DEL MODAL --- */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 print:hidden"
          onClick={closeModal} // Cerrar al hacer clic en el fondo
        >
          <div
            className="bg-gradient-to-br from-gray-800 via-gray-900 to-black border border-purple-500/30 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-auto relative p-5"
            onClick={(e) => e.stopPropagation()} // Evitar que el clic dentro cierre el modal
          >
            {/* Botón de cierre */}
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 bg-red-600 hover:bg-red-700 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors z-10 shadow-md"
              aria-label="Cerrar vista completa"
            >
              <X size={18} />
            </button>

            {/* --- BOTONES DE ZOOM (MOVIDOS AQUÍ) --- */}
            <div className="absolute bottom-16 right-4 z-20 flex flex-col gap-2 print:hidden"> {/* Usando absolute relativo al contenedor principal */}
              <button
                onClick={handleZoomIn}
                className="p-2.5 bg-gray-700/70 text-white rounded-full hover:bg-gray-600 transition-colors"
                aria-label="Acercar"
              >
                <ZoomIn size={22} />
              </button>
              <button
                onClick={handleZoomOut}
                className="p-2.5 bg-gray-700/70 text-white rounded-full hover:bg-gray-600 transition-colors"
                aria-label="Alejar"
              >
                <ZoomOut size={22} />
              </button>
            </div>
            {/* --- FIN BOTONES DE ZOOM --- */}

            {/* Contenedor del contenido específico con overflow */}
            <div className="modal-content-area overflow-auto h-full">
              {/* Wrapper interno para aplicar zoom y centrar */}
              <div
                className="inline-block origin-top-left transition-transform duration-200 ease-in-out"
                style={{ transform: `scale(${modalZoom})` }}
              >
                {/* Lógica para renderizar contenido del modal */}
                {modalContentId === 'cv-page-1' && (
                  <div id="modal-cv-page-1" className="bg-white w-[842px] h-[1191px]">
                    <TemplateComponent
                      data={{ ...cv, experience: firstPageExperiences, courses: includeCoursesInFirstPage ? cv.courses : [] } as any}
                      customColor={cv.templateColor}
                    />
                  </div>
                )}
                {/* Renderizar páginas adicionales del CV */}
                {modalContentId?.startsWith('cv-page-') && parseInt(modalContentId.split('-')[2]) > 1 && (
                  (() => {
                    const pageNum = parseInt(modalContentId.split('-')[2]);
                    const pageIndex = pageNum - 2; // Convertir número de página a índice del array `pages`
                    const pageData = pages[pageIndex + 1]; // +1 porque pages incluye la p1
                    if (!pageData) return null;

                    // Lógica para determinar cursos en esta página (similar a la lógica principal)
                    const isLastPageModal = pageIndex === pages.length - 2;
                    const shouldIncludeCoursesModal = isLastPageModal && hasCourses;
                    // TODO: Replicar lógica completa de asignación de cursos si es necesario para casos específicos
                    let coursesForPageModal = shouldIncludeCoursesModal ? cv.courses : [];
                    // ... añadir más lógica de cursos si es necesario para casos 5, 6, 7, 8 ...

                    return (
                      <div id={`modal-${modalContentId}`} className="bg-white w-[842px] h-[1191px]">
                        <TemplateComponent
                          data={{ ...cv, name: "", title: "", experience: pageData, education: [], isContinuationPage: true, courses: coursesForPageModal } as any}
                          customColor={cv.templateColor}
                        />
                      </div>
                    );
                  })()
                )}
                {modalContentId === 'letter' && (
                  <div id="modal-letter" className="bg-white p-8 w-[842px]"> {/* Aplicar padding aquí también */}
                    {renderCoverLetter()}
                  </div>
                )}
                {modalContentId === 'analysis' && (
                  <div id="modal-analysis" className="bg-white p-8 w-[842px]"> {/* Aplicar padding aquí también */}
                    {/* Re-renderizar contenido del análisis */}
                    {/* Simplificado: podrías necesitar extraer la lógica de renderizado del análisis a una función separada */}
                    {cvData.analysis.overallScore && (
                      <>
                        {/* Resumen Puntuación */}
                        <div className="mb-8 p-6 bg-gray-100 border border-gray-300 rounded-xl">
                          {/* ... HTML interno del resumen ... */}
                          <div className="flex items-center gap-4 mb-4">
                            <div className="relative">
                              <div className="w-24 h-24 rounded-full bg-blue-600 flex items-center justify-center">
                                <Trophy className="w-12 h-12 text-white" />
                              </div>
                              <div className="absolute -bottom-2 -right-2 bg-blue-700 rounded-full px-3 py-1 text-sm font-bold text-white">{cvData.analysis.overallScore}/10</div>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-gray-900 mb-2">{cvData.cv.title}</h3>
                              <div className="flex items-center gap-2 mb-3">
                                {renderStars(cvData.analysis.overallScore)}
                              </div>
                              <p className="text-gray-700 text-sm">Compatibilidad con el puesto</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <div className="bg-white p-4 rounded-lg border border-gray-300">
                              <h4 className="text-green-700 font-semibold mb-2 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-700"></div>Puntos Fuertes
                              </h4>
                              <ul className="space-y-1">
                                {cvData.analysis.strengths.map((strength: string, index: number) => (
                                  <li key={index} className="text-sm text-gray-800 flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-700"></div>{strength}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="bg-white p-4 rounded-lg border border-gray-300">
                              <h4 className="text-red-700 font-semibold mb-2 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-red-700"></div>Áreas de Mejora
                              </h4>
                              <ul className="space-y-1">
                                {cvData.analysis.weaknesses.map((weakness: string, index: number) => (
                                  <li key={index} className="text-sm text-gray-800 flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-700"></div>{weakness}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                        {/* Grid Mejoras */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                          {cvData.analysis.improvements.map((improvement: any, index: number) => (
                            <div key={index} className="relative bg-white border border-gray-300 rounded-lg p-4 sm:p-6">
                              {/* ... HTML interno de la mejora ... */}
                              <div className={`absolute top-4 right-4 w-2 h-2 rounded-full ${getPriorityColor(improvement.priority)}`}></div>
                              <div className="flex items-start gap-4">
                                <div className="p-3 bg-gray-100 rounded-lg">
                                  {CATEGORY_ICONS[improvement.category as keyof typeof CATEGORY_ICONS] || <Target className="w-6 h-6 text-gray-700" />}
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{improvement.title}</h3>
                                  <p className="text-sm text-gray-700 mb-3">{improvement.description}</p>
                                  <div className="space-y-2">
                                    {improvement.recommendations.map((rec: string, idx: number) => (
                                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-800">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-700"></div>{rec}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* --- FIN ESTRUCTURA MODAL --- */}

      <div
        ref={mainContentRef}
        className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 pb-4"
      >
        {/* Encabezado con botón circular y título en la misma línea */}
        <div className="flex items-center gap-3 mb-4 md:mb-5">
          {/* Botón Volver circular y compacto */}
          <button
            onClick={() => navigate('/my-cvs')}
            className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300 backdrop-blur-sm flex items-center justify-center border border-white/5 hover:border-white/10 group hover:scale-110 flex-shrink-0"
            title="Volver a Mis CVs"
          >
            <svg className="w-5 h-5 transform group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          {/* Título y descripción con estilo futurista - MOVIDO AQUÍ */}
          <div className="relative flex-1">
            <div className="absolute -left-2 -top-1 w-12 h-12 bg-purple-500/10 rounded-full blur-lg"></div>
            <div className="absolute right-0 top-0 w-16 h-16 bg-indigo-500/10 rounded-full blur-lg"></div>

            <h1 className="text-xl md:text-2xl font-bold leading-tight relative">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-purple-200 to-blue-300">
                Vista Previa del CV
              </span>
              <span className="absolute -bottom-1 left-0 h-0.5 w-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full"></span>
            </h1>

            <p className="text-xs md:text-sm text-purple-100/70 mt-0.5 leading-relaxed">
              Previsualiza, descarga y comparte tu CV optimizado con IA
            </p>
          </div>
        </div>

        {/* CV Preview con estilo mejorado */}
        {!cvData ? (
          <div className="min-h-[50vh] flex items-center justify-center bg-gradient-to-br from-blue-900/40 via-indigo-900/40 to-purple-900/30 backdrop-blur-sm rounded-xl border border-purple-500/20 shadow-[0_8px_30px_rgba(138,75,175,0.2)]">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-50 blur-lg transform scale-150 animate-pulse"></div>
              <div className="animate-spin relative rounded-full h-12 w-12 border-2 border-t-purple-500 border-r-blue-500 border-b-indigo-500 border-l-transparent"></div>
            </div>
          </div>
        ) : (
          <div className="relative mt-4 md:mt-6">  {/* Reducido margen superior */}
            {/* Efecto de resplandor decorativo */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-purple-500/10 rounded-full blur-xl print:hidden"></div>
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-blue-500/10 rounded-full blur-xl print:hidden"></div>

            {/* Encabezado para la sección del CV */}
            <div className="relative z-10 mb-3 md:mb-4">  {/* Reducido margen inferior */}
              {/* Contenedor principal del encabezado: flex-col en móvil, sm:flex-row en escritorio */}
              <div className="flex flex-col items-center sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* Grupo Título (Icono + Texto) */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center shadow-[0_0_10px_rgba(138,75,175,0.3)]">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">
                      Curriculum Vitae
                    </h2>
                    <p className="text-purple-100/70 text-xs md:text-sm mt-0.5">
                      Tu CV optimizado con IA para el puesto solicitado
                    </p>
                  </div>
                </div>
                {/* Grupo Botones: Fila única y centrado en móvil, wrap/end en escritorio */}
                <div className="flex gap-2 w-full justify-center sm:flex-wrap sm:gap-3 sm:w-auto sm:justify-end">
                  <button
                    onClick={openEditModal}
                    className="group relative px-6 py-2 bg-gradient-to-r from-blue-600/80 to-blue-700/80 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,130,246,0.3)] flex items-center gap-2 overflow-hidden w-full sm:w-auto justify-center"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600 opacity-0 group-hover:opacity-20 blur-md transition-opacity"></span>
                    <Edit3 className="w-5 h-5 relative z-10" />
                    <span className="relative z-10">Editar</span>
                  </button>

                  <button
                    disabled={isExporting}
                    onClick={() => handleExport('pdf')}
                    className="group relative px-6 py-2 bg-gradient-to-r from-red-600/80 to-red-700/80 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(239,68,68,0.3)] flex items-center gap-2 overflow-hidden w-full sm:w-auto justify-center"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-red-400 to-red-600 opacity-0 group-hover:opacity-20 blur-md transition-opacity"></span>
                    <FileText className="w-5 h-5 relative z-10" />
                    <span className="relative z-10">PDF</span>
                  </button>

                  <button
                    disabled={isExporting}
                    onClick={() => handleExport('images')}
                    className="group relative px-6 py-2 bg-gradient-to-r from-green-600/80 to-green-700/80 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)] flex items-center gap-2 overflow-hidden w-full sm:w-auto justify-center"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 opacity-0 group-hover:opacity-20 blur-md transition-opacity"></span>
                    <ImagePlus className="w-5 h-5 relative z-10" />
                    <span className="relative z-10">Imágenes</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Contenedor exterior para la primera página */}
            <div className="relative">
              <div
                ref={firstPageScrollRef}
                className="cv-preview-wrapper relative md:max-w-none overflow-x-hidden overflow-y-hidden md:overflow-visible
-                            md:h-auto md:block /* Eliminado h-[700px] */
+                            h-[536px] md:h-auto md:block /* Ajustado h-[700px] a h-[536px] */
                            ">
                {/* Botón Expandir (visible solo en móvil) */}
                <button
                  onClick={() => openModal('cv-page-1')}
                  className="md:hidden absolute top-2 right-2 z-20 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                  aria-label="Ver vista completa Página 1"
                >
                  <Maximize2 size={16} />
                </button>
                {/* Contenedor interior escalado */}
                <div
                  id="cv-preview"
                  className="cv-preview bg-white shadow-xl overflow-hidden
                             transform scale-[0.45] md:scale-100 origin-top-left /* Escala en movil */
                             w-[842px]  /* Ancho base A4 */
                             h-[1191px] /* Alto base A4 */
                             "
                  style={{
                    fontFamily: cv.fontFamily || 'Arial, sans-serif',
                    fontSize: `${cv.fontSize || 14}px`
                  }}
                >
                  <TemplateComponent
                    data={{
                      ...cv,
                      experience: firstPageExperiences,
                      // Controlamos si incluimos o no los cursos en la primera página
                      courses: includeCoursesInFirstPage ? cv.courses : [],
                      fontFamily: cv.fontFamily,
                      fontSize: cv.fontSize
                    } as any}
                    customColor={cv.templateColor}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Páginas adicionales */}
        {finalPages.slice(1).map((pageExperiences, pageIndex) => {
          console.log(`Renderizando página ${pageIndex + 2} con ${pageExperiences.length} experiencias y ${cv.courses?.length || 0} cursos`);

          // Determinar si esta página debe incluir cursos
          const isLastPage = pageIndex === finalPages.length - 2;
          const shouldIncludeCourses = isLastPage && hasCourses;

          // Determinar si es la segunda página del caso 2 (solo cursos)
          const isCase2SecondPage = currentCase === DISTRIBUTION_CASES.CASE_2 && pageIndex === 0;

          // Determinar si es la segunda página del caso 3 (tercer empleo + cursos)
          const isCase3SecondPage = currentCase === DISTRIBUTION_CASES.CASE_3 && pageIndex === 0;

          // Determinar si es la segunda página del caso 4 (tercer y cuarto empleo + cursos)
          const isCase4SecondPage = currentCase === DISTRIBUTION_CASES.CASE_4 && pageIndex === 0;

          // Determinar si es la segunda página del caso 5 (3 experiencias)
          const isCase5SecondPage = currentCase === DISTRIBUTION_CASES.CASE_5 && pageIndex === 0;

          // Determinar si es la tercera página del caso 5 (solo cursos)
          const isCase5ThirdPage = currentCase === DISTRIBUTION_CASES.CASE_5 && pageIndex === 1;

          // Determinar si es la segunda o tercera página del caso 6
          const isCase6SecondPage = currentCase === DISTRIBUTION_CASES.CASE_6 && pageIndex === 0;
          const isCase6ThirdPage = currentCase === DISTRIBUTION_CASES.CASE_6 && pageIndex === 1;

          // Determinar si es la segunda o tercera página del caso 7
          const isCase7SecondPage = currentCase === DISTRIBUTION_CASES.CASE_7 && pageIndex === 0;
          const isCase7ThirdPage = currentCase === DISTRIBUTION_CASES.CASE_7 && pageIndex === 1;

          // Determinar si es la segunda, tercera o cuarta página del caso 8
          const isCase8SecondPage = currentCase === DISTRIBUTION_CASES.CASE_8 && pageIndex === 0;
          const isCase8ThirdPage = currentCase === DISTRIBUTION_CASES.CASE_8 && pageIndex === 1;
          const isCase8FourthPage = currentCase === DISTRIBUTION_CASES.CASE_8 && pageIndex === 2;

          // Verificar si es cualquier página de continuación específica
          const isSpecialCaseContinuationPage = isCase3SecondPage || isCase4SecondPage ||
            (currentCase === DISTRIBUTION_CASES.CASE_5 && (pageIndex === 0 || pageIndex === 1)) ||
            (currentCase === DISTRIBUTION_CASES.CASE_6 && (pageIndex === 0 || pageIndex === 1)) ||
            (currentCase === DISTRIBUTION_CASES.CASE_7 && (pageIndex === 0 || pageIndex === 1)) ||
            (currentCase === DISTRIBUTION_CASES.CASE_8 && (pageIndex === 0 || pageIndex === 1 || pageIndex === 2));

          // Verificar si es una página que solo debe contener cursos
          const isOnlyCoursesPage = isCase2SecondPage || isCase5ThirdPage || isCase8FourthPage;

          // Determinar si esta página debe incluir cursos (Caso 5 - tercera página)
          const isCase5CoursesPage = currentCase === DISTRIBUTION_CASES.CASE_5 && pageIndex === 1;

          // Determinar si esta página debe incluir cursos (Caso 7 - tercera página)
          const isCase7CoursesPage = currentCase === DISTRIBUTION_CASES.CASE_7 && pageIndex === 1;

          // Determinar si esta página debe incluir cursos (Caso 8 - cuarta página)
          const isCase8CoursesPage = currentCase === DISTRIBUTION_CASES.CASE_8 && pageIndex === 2;

          // Determinar si es el caso especial del CV odontólogo con 4 experiencias
          const isOdontologoSpecialCase = currentCase === DISTRIBUTION_CASES.CASE_5 &&
            cv.experience.length === 4 &&
            cv.title && cv.title.toLowerCase().includes('odontólog');

          // Determinar si es una página de continuación que incluye experiencias
          const isContinuationWithExperiences = (isCase3SecondPage || isCase4SecondPage || isCase5SecondPage ||
            isCase6SecondPage || isCase6ThirdPage || isCase7SecondPage ||
            isCase7ThirdPage || isCase8SecondPage || isCase8ThirdPage) &&
            pageExperiences && pageExperiences.length > 0;

          console.log(`Página ${pageIndex + 2} - Caso: ${currentCase}, ¿Es última página? ${isLastPage}, ¿Incluir cursos? ${shouldIncludeCourses || isCase5CoursesPage || isCase7CoursesPage || isCase8CoursesPage}, ¿Es página solo de cursos? ${isOnlyCoursesPage}, ¿Es caso especial odontólogo? ${isOdontologoSpecialCase}`);

          // Asegurar que los cursos estén disponibles - copia profunda para evitar referencias
          let coursesForPage: any[] = [];

          // Solo incluir cursos si es la última página, una página específica de solo cursos, o es la tercera página del caso 5/7
          if ((shouldIncludeCourses || isCase2SecondPage || isCase3SecondPage || isCase4SecondPage ||
            isCase7CoursesPage || isCase8CoursesPage) &&
            !isCase5SecondPage && !isCase7SecondPage && !isCase8SecondPage && !isCase8ThirdPage) {
            coursesForPage = cv.courses ? JSON.parse(JSON.stringify(cv.courses)) : [];
            console.log(`Cursos asignados a página ${pageIndex + 2}:`, coursesForPage.length);
          }

          // Para el caso 5, asignar cursos solo a la tercera página
          if (currentCase === DISTRIBUTION_CASES.CASE_5) {
            if (pageIndex === 1) { // Tercera página (índice 1 = página 3)
              coursesForPage = cv.courses ? JSON.parse(JSON.stringify(cv.courses)) : [];
              console.log(`Cursos asignados a la tercera página del caso 5:`, coursesForPage.length);
            } else if (pageIndex === 0) { // Segunda página (índice 0 = página 2)
              coursesForPage = []; // No incluir cursos en la segunda página del caso 5
              console.log(`Caso 5: No asignando cursos a la segunda página`);
            }
          }

          // Para el caso 6, asignar cursos solo a la tercera página
          if (currentCase === DISTRIBUTION_CASES.CASE_6) {
            if (pageIndex === 1) { // Tercera página (índice 1 = página 3)
              coursesForPage = cv.courses ? JSON.parse(JSON.stringify(cv.courses)) : [];
              console.log(`Cursos asignados a la tercera página del caso 6:`, coursesForPage.length);
            } else if (pageIndex === 0) { // Segunda página (índice 0 = página 2)
              coursesForPage = []; // No incluir cursos en la segunda página del caso 6
              console.log(`Caso 6: No asignando cursos a la segunda página`);
            }
          }

          // Para el caso 7, asignar cursos solo a la tercera página
          if (currentCase === DISTRIBUTION_CASES.CASE_7) {
            if (pageIndex === 1) { // Tercera página (índice 1 = página 3)
              coursesForPage = cv.courses ? JSON.parse(JSON.stringify(cv.courses)) : [];
              console.log(`Cursos asignados a la tercera página del caso 7:`, coursesForPage.length);
            } else if (pageIndex === 0) { // Segunda página (índice 0 = página 2)
              coursesForPage = []; // No incluir cursos en la segunda página del caso 7
              console.log(`Caso 7: No asignando cursos a la segunda página`);
            }
          }

          // Para el caso 8, asignar cursos solo a la cuarta página
          if (currentCase === DISTRIBUTION_CASES.CASE_8) {
            if (pageIndex === 2) { // Cuarta página (índice 2 = página 4)
              coursesForPage = cv.courses ? JSON.parse(JSON.stringify(cv.courses)) : [];
              console.log(`Cursos asignados a la cuarta página del caso 8:`, coursesForPage.length);
            } else if (pageIndex === 0 || pageIndex === 1) { // Segunda y tercera página
              coursesForPage = []; // No incluir cursos en la segunda y tercera página del caso 8
              console.log(`Caso 8: No asignando cursos a la página ${pageIndex + 2}`);
            }
          }

          return (
            <div key={`page-${pageIndex + 2}`} className="relative mt-0.5 md:mt-1">  {/* Minimizar espaciado entre páginas */}
              {/* Wrapper */}
              <div className="relative">
                <div
                  className="cv-preview-wrapper additional-page-scroll-wrapper relative md:max-w-none overflow-x-hidden overflow-y-hidden md:overflow-visible
-                              md:h-auto md:block /* Eliminado h-[700px] */
+                              h-[536px] md:h-auto md:block /* Ajustado h-[700px] a h-[536px] */
                              ">
                  {/* Botón Expandir (visible solo en móvil) */}
                  <button
                    onClick={() => openModal(`cv-page-${pageIndex + 2}`)}
                    className="md:hidden absolute top-2 right-2 z-20 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    aria-label={`Ver vista completa Página ${pageIndex + 2}`}
                  >
                    <Maximize2 size={16} />
                  </button>
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-xl print:hidden md:hidden"></div>
                  {/* Contenedor interior escalado */}
                  <div
                    id={`cv-preview-page${pageIndex + 2}`}
                    className="cv-preview bg-white shadow-xl overflow-hidden
                                transform scale-[0.45] md:scale-100 origin-top-left /* Escala en movil */
                               w-[842px]  /* Ancho base A4 */
                               h-[1191px] /* Alto base A4 */
                               "
                    style={{
                      fontFamily: cv.fontFamily || 'Arial, sans-serif',
                      fontSize: `${cv.fontSize || 14}px`
                    }}
                  >
                    <TemplateComponent
                      data={{
                        ...cv,
                        // No mostrar nombre en páginas de continuación
                        name: isSpecialCaseContinuationPage ? "" : cv.name,
                        // No mostrar título en páginas de continuación
                        title: isSpecialCaseContinuationPage ? "" : cv.title,
                        // No incluir experiencias en páginas de solo cursos
                        experience: isOnlyCoursesPage ? [] : pageExperiences,
                        education: [], // No mostrar educación en las páginas adicionales
                        isContinuationPage: true,
                        // Flags específicos para cada caso
                        isCase3ContinuationPage: isCase3SecondPage,
                        isCase4ContinuationPage: isCase4SecondPage,
                        isCase5SecondPage: isCase5SecondPage,
                        isCase5ThirdPage: isCase5ThirdPage,
                        isCase6SecondPage: isCase6SecondPage,
                        isCase6ThirdPage: isCase6ThirdPage,
                        isCase7SecondPage: isCase7SecondPage,
                        isCase7ThirdPage: isCase7ThirdPage,
                        isCase8SecondPage: isCase8SecondPage,
                        isCase8ThirdPage: isCase8ThirdPage,
                        isCase8FourthPage: isCase8FourthPage,
                        // Flag para páginas de continuación que incluyen experiencias
                        isContinuationWithExperiences: isContinuationWithExperiences,
                        // Incluir cursos según corresponda
                        courses: coursesForPage,
                        template: cv.template,
                        // Flag adicional para indicar si es una página de solo cursos
                        isOnlyCoursesPage: isOnlyCoursesPage,
                        fontFamily: cv.fontFamily,
                        fontSize: cv.fontSize
                      } as any}
                      customColor={cv.templateColor}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Carta de presentación con estilo glassmorphism */}
        {(cvData?.coverLetter || isCoverLetterLoading) && (
          <div className="mt-4 md:mt-6 relative">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-8 -left-8 w-28 h-28 bg-purple-500/10 rounded-full blur-xl"></div>

            <div className="relative z-10">
              {/* Header colapsable */}
              <div className="mb-3 md:mb-4">
                <CollapsibleSectionHeader
                  title="Carta de Presentación"
                  subtitle={
                    isCoverLetterLoading
                      ? "🔄 Generando carta personalizada con IA..."
                      : "Carta personalizada que destaca tu perfil para el puesto"
                  }
                  icon={<FileText className="w-5 h-5 text-white" />}
                  isExpanded={isCoverLetterExpanded}
                  onToggle={() => setIsCoverLetterExpanded(!isCoverLetterExpanded)}
                  actionButtons={
                    cvData?.coverLetter ? (
                      <>
                        <button
                          onClick={openEditLetterModal}
                          className="group relative px-4 py-2 bg-gradient-to-r from-blue-600/80 to-blue-700/80 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(59,130,246,0.3)] flex items-center justify-center gap-2 overflow-hidden"
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600 opacity-0 group-hover:opacity-20 blur-md transition-opacity"></span>
                          <Edit3 className="w-4 h-4 relative z-10" />
                          <span className="relative z-10">Editar</span>
                        </button>

                        <button
                          onClick={() => handleExport('coverLetterPDF')}
                          className="group relative px-4 py-2 bg-gradient-to-r from-red-600/80 to-red-700/80 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(239,68,68,0.3)] flex items-center justify-center gap-2 overflow-hidden"
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-red-400 to-red-600 opacity-0 group-hover:opacity-20 blur-md transition-opacity"></span>
                          <FileText className="w-4 h-4 relative z-10" />
                          <span className="relative z-10">PDF</span>
                        </button>

                        <button
                          disabled={isExporting}
                          onClick={() => handleExport('coverLetterImages')}
                          className="group relative px-4 py-2 bg-gradient-to-r from-green-600/80 to-green-700/80 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)] flex items-center justify-center gap-2 overflow-hidden"
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 opacity-0 group-hover:opacity-20 blur-md transition-opacity"></span>
                          <ImagePlus className="w-4 h-4 relative z-10" />
                          <span className="relative z-10">Imágenes</span>
                        </button>
                      </>
                    ) : undefined
                  }
                  gradientFrom="from-blue-600"
                  gradientTo="to-purple-700"
                />
              </div>

              {/* Contenido colapsable con animación */}
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${isCoverLetterExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                {/* Wrapper para la Carta con scroll y altura fija en móvil */}
                <div className="relative letter-analysis-wrapper md:max-w-none overflow-y-hidden md:overflow-visible h-[500px] md:h-auto md:block overflow-x-hidden mt-4">
                  {/* Botón Expandir (visible solo en móvil) */}
                  <button
                    onClick={() => openModal('letter')}
                    className="md:hidden absolute top-2 right-2 z-20 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    aria-label="Ver vista completa Carta de Presentación"
                  >
                    <Maximize2 size={16} />
                  </button>
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500/30 to-blue-500/30 rounded-xl blur opacity-75 group-hover:opacity-100 transition-opacity duration-500 group-hover:duration-200 animate-tilt print:hidden md:hidden"></div>
                  {/* Contenido escalado */}
                  <div
                    id="cover-letter-preview"
                    className="relative bg-white/95 backdrop-blur-sm shadow-lg p-8 border border-purple-500/10 hover:border-purple-500/20 transition-all duration-300 hover:shadow-[0_10px_40px_rgba(138,75,175,0.15)]
                               transform scale-[0.4] md:scale-100 origin-top-left
                               w-[842px]
                               overflow-hidden
                               md:mx-auto
                              "
                  >
                    {isCoverLetterLoading ? (
                      <CoverLetterSkeleton />
                    ) : (
                      renderCoverLetter()
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Preview con estilo glassmorphism y mejor compatibilidad con PDF */}
        {(cvData?.analysis || isAnalysisLoading) && (
          <div className="mt-4 md:mt-6 relative">
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-blue-500/10 rounded-full blur-xl"></div>

            <div className="relative z-10">
              {/* Header colapsable */}
              <div className="mb-3 md:mb-4">
                <CollapsibleSectionHeader
                  title="Análisis de Mejoras"
                  subtitle={
                    isAnalysisLoading
                      ? "🔄 Analizando tu perfil y generando recomendaciones..."
                      : "Recomendaciones personalizadas para potenciar tu perfil profesional"
                  }
                  icon={<TrendingUp className="w-5 h-5 text-white" />}
                  isExpanded={isAnalysisSectionExpanded}
                  onToggle={() => setIsAnalysisSectionExpanded(!isAnalysisSectionExpanded)}
                  actionButtons={
                    cvData?.analysis ? (
                      <>
                        <button
                          onClick={() => handleExport('analysisPDF')}
                          className="group relative px-4 py-2 bg-gradient-to-r from-red-600/80 to-red-700/80 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(239,68,68,0.3)] flex items-center justify-center gap-2 overflow-hidden"
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-red-400 to-red-600 opacity-0 group-hover:opacity-20 blur-md transition-opacity"></span>
                          <FileText className="w-4 h-4 relative z-10" />
                          <span className="relative z-10">PDF</span>
                        </button>

                        <button
                          disabled={isExporting}
                          onClick={() => handleExport('analysisImages')}
                          className="group relative px-4 py-2 bg-gradient-to-r from-green-600/80 to-green-700/80 text-white rounded-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)] flex items-center justify-center gap-2 overflow-hidden"
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 opacity-0 group-hover:opacity-20 blur-md transition-opacity"></span>
                          <ImagePlus className="w-4 h-4 relative z-10" />
                          <span className="relative z-10">Imágenes</span>
                        </button>
                      </>
                    ) : undefined
                  }
                  gradientFrom="from-purple-600"
                  gradientTo="to-blue-700"
                />
              </div>

              {/* Contenido colapsable con animación */}
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${isAnalysisSectionExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                {/* Wrapper para el Análisis con scroll y altura fija en móvil */}
                <div className="relative letter-analysis-wrapper md:max-w-none overflow-y-hidden md:overflow-visible h-[500px] md:h-auto md:block overflow-x-hidden mt-4">
                  {/* Botón Expandir (visible solo en móvil) */}
                  <button
                    onClick={() => openModal('analysis')}
                    className="md:hidden absolute top-2 right-2 z-20 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    aria-label="Ver vista completa Análisis de Mejoras"
                  >
                    <Maximize2 size={16} />
                  </button>
                  {/* Contenido descargable escalado */}
                  <div
                    id="analysis-section"
                    className="backdrop-blur-lg bg-gradient-to-br from-white/10 to-white/5 p-8 print:bg-white print:text-black border border-purple-500/10 hover:border-purple-500/20 transition-all duration-300 hover:shadow-[0_10px_40px_rgba(138,75,175,0.15)]
                             transform scale-[0.4] md:scale-100 origin-top-left /* Escala reducida a 0.4 */
                             w-[842px] /* Ancho fijo A4 */ 
                             overflow-hidden /* Evitar desbordamiento horizontal */
                             md:mx-auto /* Centrar en desktop */
                            "
                    style={{ backgroundColor: 'white', color: '#1a202c' }}
                  >
                    {isAnalysisLoading ? (
                      <AnalysisSkeleton />
                    ) : (
                      <>
                        {/* Resumen de Puntuación */}
                        <div className="mb-8 p-6 bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-md rounded-xl border border-white/10 relative overflow-hidden group print:bg-gray-100 print:border-gray-300"
                          style={{ backgroundColor: '#f8f9fa', border: '1px solid #e2e8f0' }}>
                          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                          <div className="flex items-center gap-4 mb-4 relative z-10">
                            <div className="relative">
                              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-30 blur-md transform scale-125 animate-pulse print:hidden"></div>
                              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center relative print:bg-blue-600"
                                style={{ backgroundColor: '#6d28d9' }}>
                                <Trophy className="w-12 h-12 text-white" />
                              </div>
                              <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full px-3 py-1 text-sm font-bold text-white shadow-[0_0_10px_rgba(138,75,175,0.3)] print:bg-blue-700 print:shadow-none"
                                style={{ backgroundColor: '#4c1d95', color: 'white', fontWeight: 'bold' }}>
                                {cvData.analysis.overallScore}/10
                              </div>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-bold text-white mb-2 print:text-gray-900"
                                style={{ color: '#1a202c', fontWeight: 'bold' }}>{cvData.cv.title}</h3>
                              <div className="flex items-center gap-2 mb-3">
                                {renderStars(cvData.analysis.overallScore)}
                              </div>
                              <p className="text-gray-300 text-sm print:text-gray-700"
                                style={{ color: '#4a5568' }}>
                                Compatibilidad con el puesto
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 relative z-10">
                            <div className="bg-white/5 p-4 rounded-lg border border-white/10 print:bg-white print:border-gray-300"
                              style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem' }}>
                              <h4 className="text-green-400 font-semibold mb-2 flex items-center gap-2 print:text-green-700"
                                style={{ color: '#15803d', fontWeight: 600 }}>
                                <div className="w-2 h-2 rounded-full bg-green-400 print:bg-green-700"
                                  style={{ backgroundColor: '#15803d', width: '8px', height: '8px', borderRadius: '9999px' }}></div>
                                Puntos Fuertes
                              </h4>
                              <ul className="space-y-1">
                                {(cvData?.analysis?.strengths || []).map((strength: string, index: number) => (
                                  <li key={index} className="text-sm text-gray-300 flex items-center gap-2 group/item print:text-gray-800"
                                    style={{ color: '#1a202c', fontSize: '0.875rem' }}>
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 group-hover/item:scale-125 transition-transform print:bg-green-700"
                                      style={{ backgroundColor: '#15803d', width: '6px', height: '6px', borderRadius: '9999px' }}></div>
                                    {strength}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="bg-white/5 p-4 rounded-lg border border-white/10 print:bg-white print:border-gray-300"
                              style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem' }}>
                              <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2 print:text-red-700"
                                style={{ color: '#b91c1c', fontWeight: 600 }}>
                                <div className="w-2 h-2 rounded-full bg-red-400 print:bg-red-700"
                                  style={{ backgroundColor: '#b91c1c', width: '8px', height: '8px', borderRadius: '9999px' }}></div>
                                Áreas de Mejora
                              </h4>
                              <ul className="space-y-1">
                                {(cvData?.analysis?.weaknesses || []).map((weakness: string, index: number) => (
                                  <li key={index} className="text-sm text-gray-300 flex items-center gap-2 group/item print:text-gray-800"
                                    style={{ color: '#1a202c', fontSize: '0.875rem' }}>
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 group-hover/item:scale-125 transition-transform print:bg-red-700"
                                      style={{ backgroundColor: '#b91c1c', width: '6px', height: '6px', borderRadius: '9999px' }}></div>
                                    {weakness}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Grid de mejoras */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                          {(cvData?.analysis?.improvements || []).map((improvement: any, index: number) => (
                            <div
                              key={index}
                              className="relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-md rounded-lg p-4 sm:p-6 border border-white/10 hover:border-blue-500/50 transition-all duration-300 group hover:shadow-[0_0_15px_rgba(138,75,175,0.15)] print:bg-white print:border-gray-300"
                              style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem' }}
                            >
                              <div className={`absolute top-4 right-4 w-2 h-2 rounded-full ${getPriorityColor(improvement.priority)}`}
                                style={{
                                  backgroundColor: improvement.priority === 'high' ? '#ef4444' :
                                    improvement.priority === 'medium' ? '#eab308' :
                                      improvement.priority === 'low' ? '#22c55e' : '#6b7280',
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '9999px',
                                  position: 'absolute',
                                  top: '1rem',
                                  right: '1rem'
                                }}></div>
                              <div className="flex items-start gap-4">
                                <div className="p-3 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors print:bg-gray-100"
                                  style={{ backgroundColor: '#f3f4f6', padding: '0.75rem', borderRadius: '0.5rem' }}>
                                  {CATEGORY_ICONS[improvement.category as keyof typeof CATEGORY_ICONS] ||
                                    <Target className="w-6 h-6 text-gray-400 print:text-gray-700" />}
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-purple-300 transition-colors print:text-gray-900"
                                    style={{ color: '#1a202c', fontWeight: 600, fontSize: '1.125rem' }}>
                                    {improvement.title}
                                  </h3>
                                  <p className="text-sm text-gray-400 mb-3 print:text-gray-700"
                                    style={{ color: '#4b5563', fontSize: '0.875rem' }}>
                                    {improvement.description}
                                  </p>
                                  <div className="space-y-2">
                                    {improvement.recommendations.map((rec: string, idx: number) => (
                                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-300 group-hover:text-gray-200 transition-colors print:text-gray-800"
                                        style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 group-hover:scale-125 transition-transform print:bg-blue-700"
                                          style={{ backgroundColor: '#3b82f6', width: '6px', height: '6px', borderRadius: '9999px' }}></div>
                                        {rec}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Fin del contenido colapsable */}
              </div>
            </div>
          </div>
        )}

        {/* ChatBot de ayuda para postulaciones */}
        <div className="mt-4 md:mt-6 relative flex justify-center w-full">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-8 -left-8 w-28 h-28 bg-purple-500/10 rounded-full blur-xl"></div>

          <div className="relative z-10 w-full mx-auto md:w-[842px]">
            {/* Header colapsable */}
            <div className="mb-3 md:mb-4">
              <CollapsibleSectionHeader
                title="Asistente de Postulación"
                subtitle="Te ayudo a responder las preguntas de tu postulación laboral"
                icon={<MessageCircle className="w-4 h-4 text-white" />}
                isExpanded={isAssistantExpanded}
                onToggle={() => setIsAssistantExpanded(!isAssistantExpanded)}
                gradientFrom="from-indigo-600"
                gradientTo="to-purple-700"
              />
            </div>

            {/* Contenido colapsable con animación */}
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${isAssistantExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
              {/* ChatBot */}
              <div className="bg-purple-950 rounded-xl overflow-hidden shadow-xl border border-purple-500/20 w-full max-w-full md:max-w-[842px] mx-auto">
                {/* Barra superior con botón maximize */}
                <div className="bg-purple-900 p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Curvia AI</h3>
                  </div>
                  <button
                    className="p-2 rounded-full hover:bg-white/10 flex items-center justify-center transition-all"
                    onClick={() => openChatFullscreen()}
                  >
                    <Maximize2 className="w-5 h-5 text-white" />
                  </button>
                </div>

                <div className="flex flex-col">
                  {/* Área de chat - Mensajes */}
                  <div id="chat-messages" className="min-h-[300px] max-h-[400px] overflow-y-auto p-3 space-y-4">
                    {/* Mensaje inicial del asistente - SIMPLIFICADO */}
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-md flex-shrink-0 overflow-hidden">
                        <img src="/img/icono_curriculab.png" alt="Asistente" className="w-full h-full object-cover" />
                      </div>
                      <div className="bg-indigo-900 rounded-2xl rounded-tl-none px-5 py-4 max-w-[75%] sm:max-w-[70%] relative group">
                        <p className="text-white pr-6 leading-relaxed">
                          ¡Hola {cvData?.cv?.name?.split(' ')[0] || 'Usuario'}! Soy tu asistente de postulación. Puedes escribirme cualquier pregunta que te hagan en tu proceso de postulación y te ayudaré a preparar la mejor respuesta. ¡Escribe tu primera pregunta! 😊
                        </p>
                        <button
                          onClick={() => copyToClipboard(`¡Hola ${cvData?.cv?.name?.split(' ')[0] || 'Usuario'}! Soy tu asistente de postulación. Puedes escribirme cualquier pregunta que te hagan en tu proceso de postulación y te ayudaré a preparar la mejor respuesta. ¡Escribe tu primera pregunta! 😊`)}
                          className="absolute top-3 right-3 text-white/60 hover:text-white/90 transition-colors"
                          title="Copiar texto"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Campo de entrada y botón de envío */}
                  <div className="p-5 border-t border-purple-800/40 bg-purple-900">
                    <div className="relative">
                      <input
                        id="user-message"
                        className="w-full bg-purple-800/70 text-white rounded-full py-4 pl-5 pr-14 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-purple-300/70"
                        placeholder="Escribe tu pregunta de postulación aquí..."
                      />
                      <button
                        id="send-message"
                        disabled={isProcessing}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-blue-500 hover:bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center transition-all"
                      >
                        {isProcessing ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="-rotate-45"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Fin del contenido colapsable del Asistente */}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de edición del CV */}
      {isEditModalOpen && editedCvData && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex flex-col">
          {/* Header del modal mejorado */}
          <div className="bg-gradient-to-r from-purple-900 to-blue-900 border-b border-purple-500/30 p-3 md:p-4">
            {/* Título principal centrado */}
            <div className="text-center mb-3 md:mb-4">
              <h2 className="text-lg md:text-xl font-bold text-white flex items-center justify-center gap-2">
                <Edit3 className="w-5 h-5 md:w-6 md:h-6" />
                Editando CV
              </h2>
              <p className="text-purple-200 text-xs md:text-sm">
                Página {currentEditPage} de {finalPages.length || 1}
              </p>
            </div>

            {/* Botón Ver Vista Previa en móvil - MOVIDO AQUÍ ARRIBA */}
            <div className="md:hidden mb-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {showPreview ? 'Volver a Editar' : 'Ver Vista Previa'}
              </button>
            </div>

            {/* Barra de controles organizada */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
              {/* Navegación entre páginas */}
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentEditPage(Math.max(1, currentEditPage - 1))}
                  disabled={currentEditPage === 1}
                  className="px-3 py-1.5 bg-purple-700 hover:bg-purple-600 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors text-sm"
                >
                  ← Anterior
                </button>

                <span className="text-white bg-purple-800 px-3 py-1.5 rounded-lg text-sm font-medium">
                  {currentEditPage} / {finalPages.length || 1}
                </span>

                <button
                  onClick={() => setCurrentEditPage(Math.min(finalPages.length || 1, currentEditPage + 1))}
                  disabled={currentEditPage >= (finalPages.length || 1)}
                  className="px-3 py-1.5 bg-purple-700 hover:bg-purple-600 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors text-sm"
                >
                  Siguiente →
                </button>
              </div>

              {/* Controles de acción - REDUCIDO ANCHO */}
              <div className="flex items-center justify-center gap-2">
                {isUnsavedChanges && (
                  <span className="bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded-full text-xs border border-yellow-500/30">
                    Sin guardar
                  </span>
                )}

                <button
                  onClick={handleSaveChanges}
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1.5 rounded-lg flex items-center gap-1 transition-colors text-sm"
                >
                  <Save className="w-4 h-4" />
                  <span className="hidden sm:inline">Guardar</span>
                </button>

                <button
                  onClick={closeEditModal}
                  className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Contenido del modal - Layout responsivo */}
          <div className="flex flex-1 overflow-hidden">
            {/* En móvil: Estado para alternar vista */}
            <>
              {/* Panel de edición */}
              <div className={`${showPreview ? 'hidden md:block' : 'block'} w-full md:w-1/3 bg-gray-900 border-r border-purple-500/20 overflow-y-auto`}>
                <div className="p-4 space-y-6 pb-20">  {/* Agregado pb-20 para padding inferior */}
                  {/* Solo mostrar información personal y contacto en la página 1 */}
                  {currentEditPage === 1 && (
                    <>
                      {/* Configuración de Fuente - Primera sección */}
                      <div className="bg-gray-800 rounded-lg p-4 mb-4">
                        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 19h16M4 5h16M4 12h16" />
                          </svg>
                          Personalización de Fuente
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Selector de fuente */}
                          <div>
                            <label className="block text-gray-300 text-sm mb-1">Tipo de fuente</label>
                            <select
                              value={selectedFont}
                              onChange={(e) => setSelectedFont(e.target.value)}
                              className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none text-sm"
                            >
                              {FONT_OPTIONS.map((font) => (
                                <option key={font.value} value={font.value} style={{ fontFamily: font.style }}>
                                  {font.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Selector de tamaño */}
                          <div>
                            <label className="block text-gray-300 text-sm mb-1">
                              Tamaño de fuente: {fontSize}px
                            </label>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setFontSize(prev => Math.max(10, prev - 1))}
                                className="bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded text-xs"
                              >
                                -
                              </button>
                              <input
                                type="range"
                                min="10"
                                max="20"
                                value={fontSize}
                                onChange={(e) => setFontSize(parseInt(e.target.value))}
                                className="flex-1 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                              />
                              <button
                                type="button"
                                onClick={() => setFontSize(prev => Math.min(20, prev + 1))}
                                className="bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded text-xs"
                              >
                                +
                              </button>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Rango: 10px - 20px
                            </div>
                          </div>
                        </div>

                        {/* Vista previa de la fuente */}
                        <div className="mt-3 p-3 bg-gray-700 rounded border border-gray-600">
                          <div className="text-gray-300 text-xs mb-1">Vista previa:</div>
                          <div
                            style={{
                              fontFamily: FONT_OPTIONS.find(f => f.value === selectedFont)?.style || 'Arial',
                              fontSize: `${fontSize}px`,
                              color: 'white'
                            }}
                          >
                            Ejemplo de texto con la fuente seleccionada
                          </div>
                        </div>
                      </div>

                      {/* Información personal */}
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Información Personal
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-gray-300 text-sm mb-1">Nombre</label>
                            <input
                              type="text"
                              value={editedCvData.name || ''}
                              onChange={(e) => updateEditedData('name', e.target.value)}
                              className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-gray-300 text-sm mb-1">Título Profesional</label>
                            <input
                              type="text"
                              value={editedCvData.title || ''}
                              onChange={(e) => updateEditedData('title', e.target.value)}
                              className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                            />
                          </div>
                          {/* Gestión de foto */}
                          <div>
                            <label className="block text-gray-300 text-sm mb-1">Foto de perfil</label>
                            <div className="space-y-3">
                              {editedCvData.photo ? (
                                <div className="flex items-center gap-3">
                                  <img
                                    src={editedCvData.photo}
                                    alt="Foto actual"
                                    className="w-16 h-16 rounded-full object-cover border border-gray-600"
                                  />
                                  <div className="flex flex-col gap-2">
                                    <button
                                      onClick={() => {
                                        const confirmDelete = window.confirm('¿Estás seguro de que quieres eliminar la foto?');
                                        if (confirmDelete) {
                                          updateEditedData('photo', null);
                                        }
                                      }}
                                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                    >
                                      🗑️ Eliminar foto
                                    </button>
                                    <label className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors cursor-pointer text-center">
                                      📷 Cambiar foto
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoUpload}
                                        className="hidden"
                                      />
                                    </label>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-3 p-3 bg-gray-700 rounded border border-gray-600">
                                    <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center">
                                      <User className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <span className="text-gray-300 text-sm">Sin foto (el CV se mostrará sin imagen)</span>
                                  </div>

                                  <label className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded text-sm transition-colors cursor-pointer text-center block">
                                    📷 Añadir foto
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={handlePhotoUpload}
                                      className="hidden"
                                    />
                                  </label>

                                  <div className="text-xs text-gray-400 bg-blue-900/30 border border-blue-500/30 rounded p-2">
                                    <p className="text-blue-300 font-medium mb-1">💡 Consejos para la foto:</p>
                                    <ul className="text-blue-200 space-y-0.5">
                                      <li>• Fondo neutro y profesional</li>
                                      <li>• Buena iluminación</li>
                                      <li>• Vestimenta formal</li>
                                      <li>• Expresión amigable</li>
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sobre mí - Solo en página 1 */}
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-white font-semibold mb-3 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Sobre mí
                          </span>
                          <div className="flex gap-2">
                            {editedCvData.about && (
                              <button
                                onClick={() => {
                                  const confirmDelete = window.confirm('¿Estás seguro de que quieres eliminar la sección "Sobre mí"?');
                                  if (confirmDelete) {
                                    updateEditedData('about', '');
                                  }
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition-colors"
                                title="Eliminar sección"
                              >
                                🗑️ Eliminar
                              </button>
                            )}
                          </div>
                        </h3>
                        <div>
                          <label className="block text-gray-300 text-sm mb-1">Descripción profesional</label>
                          <textarea
                            value={editedCvData.about || ''}
                            onChange={(e) => updateEditedData('about', e.target.value)}
                            className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none resize-none"
                            rows={4}
                            placeholder="Describe tu perfil profesional, objetivos, fortalezas clave..."
                          />
                          {/* Contador de caracteres */}
                          <div className="text-xs text-gray-400 mt-1 text-right">
                            {(editedCvData.about || '').length} caracteres
                          </div>

                          {/* Consejos */}
                          <div className="mt-3 p-2 bg-blue-900/30 border border-blue-500/30 rounded text-xs">
                            <p className="text-blue-300 font-medium mb-1">💡 Consejos:</p>
                            <ul className="text-blue-200 space-y-0.5">
                              <li>• Mantén entre 50-150 palabras</li>
                              <li>• Enfócate en tus fortalezas clave</li>
                              <li>• Menciona tu experiencia relevante</li>
                              <li>• Incluye tus objetivos profesionales</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Contacto */}
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-white font-semibold mb-3 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            Contacto
                          </span>
                          <button
                            onClick={() => {
                              // Añadir campo de contacto adicional
                              const newContactField = window.prompt('¿Qué tipo de contacto quieres añadir?', 'LinkedIn');
                              if (newContactField) {
                                const fieldKey = `contact.${newContactField.toLowerCase().replace(/\s+/g, '_')}`;
                                updateEditedData(fieldKey, '');
                              }
                            }}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs transition-colors"
                            title="Añadir campo de contacto"
                          >
                            + Añadir
                          </button>
                        </h3>
                        <div className="space-y-3">
                          {/* Email */}
                          {editedCvData.contact?.email || editedCvData.contact?.email === '' ? (
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="block text-gray-300 text-sm mb-1">Email</label>
                                <input
                                  type="email"
                                  value={editedCvData.contact?.email || ''}
                                  onChange={(e) => updateEditedData('contact.email', e.target.value)}
                                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  placeholder="ejemplo@email.com"
                                />
                              </div>
                              {editedCvData.contact?.email && (
                                <button
                                  onClick={() => {
                                    const confirmDelete = window.confirm('¿Estás seguro de que quieres eliminar el email?');
                                    if (confirmDelete) {
                                      const newContact = { ...editedCvData.contact };
                                      delete newContact.email;
                                      updateEditedData('contact', newContact);
                                    }
                                  }}
                                  className="self-end bg-red-600 hover:bg-red-700 text-white px-2 py-2 rounded text-xs transition-colors"
                                  title="Eliminar email"
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-2">
                              <button
                                onClick={() => updateEditedData('contact.email', '')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                + Añadir Email
                              </button>
                            </div>
                          )}

                          {/* Teléfono */}
                          {editedCvData.contact?.phone || editedCvData.contact?.phone === '' ? (
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="block text-gray-300 text-sm mb-1">Teléfono</label>
                                <input
                                  type="tel"
                                  value={editedCvData.contact?.phone || ''}
                                  onChange={(e) => updateEditedData('contact.phone', e.target.value)}
                                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  placeholder="+56 9 1234 5678"
                                />
                              </div>
                              {editedCvData.contact?.phone && (
                                <button
                                  onClick={() => {
                                    const confirmDelete = window.confirm('¿Estás seguro de que quieres eliminar el teléfono?');
                                    if (confirmDelete) {
                                      const newContact = { ...editedCvData.contact };
                                      delete newContact.phone;
                                      updateEditedData('contact', newContact);
                                    }
                                  }}
                                  className="self-end bg-red-600 hover:bg-red-700 text-white px-2 py-2 rounded text-xs transition-colors"
                                  title="Eliminar teléfono"
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-2">
                              <button
                                onClick={() => updateEditedData('contact.phone', '')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                + Añadir Teléfono
                              </button>
                            </div>
                          )}

                          {/* Ubicación */}
                          {editedCvData.contact?.location || editedCvData.contact?.location === '' ? (
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="block text-gray-300 text-sm mb-1">Ubicación</label>
                                <input
                                  type="text"
                                  value={editedCvData.contact?.location || ''}
                                  onChange={(e) => updateEditedData('contact.location', e.target.value)}
                                  className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  placeholder="Santiago, Chile"
                                />
                              </div>
                              {editedCvData.contact?.location && (
                                <button
                                  onClick={() => {
                                    const confirmDelete = window.confirm('¿Estás seguro de que quieres eliminar la ubicación?');
                                    if (confirmDelete) {
                                      const newContact = { ...editedCvData.contact };
                                      delete newContact.location;
                                      updateEditedData('contact', newContact);
                                    }
                                  }}
                                  className="self-end bg-red-600 hover:bg-red-700 text-white px-2 py-2 rounded text-xs transition-colors"
                                  title="Eliminar ubicación"
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-2">
                              <button
                                onClick={() => updateEditedData('contact.location', '')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                + Añadir Ubicación
                              </button>
                            </div>
                          )}

                          {/* Campos de contacto adicionales dinámicos */}
                          {editedCvData.contact && Object.entries(editedCvData.contact).map(([key, value]) => {
                            // Mostrar solo campos que no sean email, phone, location
                            if (['email', 'phone', 'location'].includes(key)) return null;

                            return (
                              <div key={key} className="flex gap-2">
                                <div className="flex-1">
                                  <label className="block text-gray-300 text-sm mb-1 capitalize">
                                    {key.replace(/_/g, ' ')}
                                  </label>
                                  <input
                                    type="text"
                                    value={value as string || ''}
                                    onChange={(e) => updateEditedData(`contact.${key}`, e.target.value)}
                                    className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  />
                                </div>
                                <button
                                  onClick={() => {
                                    const newContact = { ...editedCvData.contact };
                                    delete newContact[key];
                                    updateEditedData('contact', newContact);
                                  }}
                                  className="self-end bg-red-600 hover:bg-red-700 text-white px-2 py-2 rounded text-xs transition-colors"
                                  title="Eliminar campo"
                                >
                                  🗑️
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Educación - Solo en página 1 */}
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-white font-semibold mb-3 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                              <path d="M6 12v5c3 3 9 3 12 0v-5" />
                            </svg>
                            Educación
                          </span>
                          <button
                            onClick={() => {
                              const newEducation = [...(editedCvData.education || []), { degree: '', institution: '', period: '' }];
                              updateEditedData('education', newEducation);
                            }}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs transition-colors"
                            title="Añadir educación"
                          >
                            + Añadir
                          </button>
                        </h3>
                        <div className="space-y-3">
                          {(editedCvData.education || []).map((edu: any, index: number) => (
                            <div key={index} className="border border-gray-700 rounded p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-gray-300 text-sm">Educación {index + 1}</span>
                                <button
                                  onClick={() => {
                                    const confirmDelete = window.confirm(`¿Estás seguro de que quieres eliminar la educación "${edu.degree || 'Sin título'}"?`);
                                    if (confirmDelete) {
                                      const newEducation = [...(editedCvData.education || [])];
                                      newEducation.splice(index, 1);
                                      updateEditedData('education', newEducation);
                                    }
                                  }}
                                  className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition-colors"
                                  title="Eliminar educación"
                                >
                                  🗑️ Eliminar
                                </button>
                              </div>
                              <div className="space-y-2">
                                <input
                                  type="text"
                                  placeholder="Título o grado"
                                  value={edu.degree || ''}
                                  onChange={(e) => updateEditedData('education.degree', e.target.value, index)}
                                  className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                />
                                <input
                                  type="text"
                                  placeholder="Institución"
                                  value={edu.institution || ''}
                                  onChange={(e) => updateEditedData('education.institution', e.target.value, index)}
                                  className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                />
                                <input
                                  type="text"
                                  placeholder="Período"
                                  value={edu.period || ''}
                                  onChange={(e) => updateEditedData('education.period', e.target.value, index)}
                                  className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                />
                              </div>
                            </div>
                          ))}
                          {(!editedCvData.education || editedCvData.education.length === 0) && (
                            <div className="text-gray-500 text-center py-2">
                              <p>No hay educación registrada</p>
                              <button
                                onClick={() => {
                                  updateEditedData('education', [{ degree: '', institution: '', period: '' }]);
                                }}
                                className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                              >
                                Añadir primera educación
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Experiencias para la página actual */}
                  {(() => {
                    // Obtener todas las experiencias para tener acceso completo
                    const allExperiences = editedCvData.experience || [];

                    // Determinar qué experiencias mostrar en esta página específica
                    let experiencesToShow: any[] = [];
                    let startIndex = 0;

                    if (currentEditPage === 1) {
                      // Página 1: primeras experiencias según el caso
                      experiencesToShow = allExperiences.slice(0, finalPages[0]?.length || 0);
                      startIndex = 0;
                    } else {
                      // Páginas siguientes: calcular el offset acumulado
                      let accumulatedCount = 0;
                      for (let i = 0; i < currentEditPage - 1; i++) {
                        accumulatedCount += finalPages[i]?.length || 0;
                      }
                      startIndex = accumulatedCount;
                      const endIndex = startIndex + (finalPages[currentEditPage - 1]?.length || 0);
                      experiencesToShow = allExperiences.slice(startIndex, endIndex);
                    }

                    return (
                      <div className="bg-gray-800 rounded-lg p-4">
                        <h3 className="text-white font-semibold mb-3 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            Experiencia Laboral - Página {currentEditPage}
                          </span>
                          <button
                            onClick={() => {
                              const newExperience = {
                                title: '',
                                company: '',
                                period: '',
                                description: '',
                                achievements: []
                              };
                              const newExperiences = [...(editedCvData.experience || []), newExperience];
                              updateEditedData('experience', newExperiences);
                            }}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs transition-colors"
                            title="Añadir nueva experiencia"
                          >
                            + Añadir Experiencia
                          </button>
                        </h3>

                        <div className="space-y-4">
                          {experiencesToShow.map((exp: any, localIndex: number) => {
                            const globalIndex = startIndex + localIndex;

                            return (
                              <div key={`exp-${globalIndex}`} className="border border-gray-700 rounded p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-gray-300 text-sm">
                                    Experiencia {globalIndex + 1}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => deleteExperience(globalIndex)}
                                    className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-colors"
                                    title="Eliminar experiencia"
                                  >
                                    🗑️ Eliminar
                                  </button>
                                </div>

                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    placeholder="Título del puesto"
                                    value={allExperiences[globalIndex]?.title || ''}
                                    onChange={(e) => updateEditedData('experience.title', e.target.value, globalIndex)}
                                    className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Empresa"
                                    value={allExperiences[globalIndex]?.company || ''}
                                    onChange={(e) => updateEditedData('experience.company', e.target.value, globalIndex)}
                                    className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Período"
                                    value={allExperiences[globalIndex]?.period || ''}
                                    onChange={(e) => updateEditedData('experience.period', e.target.value, globalIndex)}
                                    className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  />
                                  <textarea
                                    placeholder="Descripción"
                                    value={allExperiences[globalIndex]?.description || ''}
                                    onChange={(e) => updateEditedData('experience.description', e.target.value, globalIndex)}
                                    className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none resize-none"
                                    rows={3}
                                  />

                                  {/* Logros/Achievements */}
                                  <div className="mt-3">
                                    <label className="block text-gray-300 text-xs mb-1">Logros (punteos)</label>
                                    <div className="space-y-1">
                                      {(allExperiences[globalIndex]?.achievements || []).map((achievement: string, achIndex: number) => (
                                        <div key={achIndex} className="flex gap-1">
                                          <input
                                            type="text"
                                            placeholder={`• Logro ${achIndex + 1}`}
                                            value={achievement || ''}
                                            onChange={(e) => updateEditedData(`experience.achievements.${achIndex}`, e.target.value, globalIndex)}
                                            className="flex-1 bg-gray-600 text-white px-2 py-1 rounded text-xs border border-gray-500 focus:border-purple-400 focus:outline-none"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const currentExp = allExperiences[globalIndex];
                                              if (!currentExp?.achievements) return;
                                              const newAchievements = [...currentExp.achievements];
                                              newAchievements.splice(achIndex, 1);
                                              updateEditedData('experience.achievements', newAchievements, globalIndex);
                                            }}
                                            className="px-1 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const currentExp = allExperiences[globalIndex];
                                          const currentAchievements = currentExp?.achievements || [];
                                          updateEditedData('experience.achievements', [...currentAchievements, ''], globalIndex);
                                        }}
                                        className="w-full px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs"
                                      >
                                        + Añadir logro
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {experiencesToShow.length === 0 && (
                            <div className="text-gray-500 text-center py-4">
                              <p className="mb-2">No hay experiencias en esta página</p>
                              {currentEditPage === 1 && (
                                <button
                                  onClick={() => {
                                    const newExperience = {
                                      title: '',
                                      company: '',
                                      period: '',
                                      description: '',
                                      achievements: []
                                    };
                                    updateEditedData('experience', [newExperience]);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                >
                                  Añadir primera experiencia
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Cursos y Actividades Adicionales - Para páginas que tengan cursos */}
                  {(() => {
                    // Determinar si esta página debe mostrar cursos
                    const isLastPage = currentEditPage === (finalPages.length || 1);
                    const hasCourses = editedCvData.courses && editedCvData.courses.length > 0;

                    // También mostrar cursos si es página 3 o la última página
                    const shouldShowCourses = hasCourses && (
                      isLastPage ||
                      currentEditPage === 3 ||
                      (currentEditPage === 2 && finalPages.length === 2)
                    );

                    if (shouldShowCourses) {
                      return (
                        <div className="bg-gray-800 rounded-lg p-4">
                          <h3 className="text-white font-semibold mb-3 flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14,2 14,8 20,8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10,9 9,9 8,9" />
                              </svg>
                              Cursos y Actividades Adicionales
                            </span>
                            <button
                              onClick={() => {
                                const newCourse = { title: '', institution: '', period: '', description: '' };
                                const newCourses = [...(editedCvData.courses || []), newCourse];
                                updateEditedData('courses', newCourses);
                              }}
                              className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs transition-colors"
                              title="Añadir nuevo curso"
                            >
                              + Añadir Curso
                            </button>
                          </h3>
                          <div className="space-y-3">
                            {(editedCvData.courses || []).map((course: any, index: number) => (
                              <div key={index} className="border border-gray-700 rounded p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-gray-300 text-sm">Curso {index + 1}</span>
                                  <button
                                    type="button"
                                    onClick={() => deleteCourse(index)}
                                    className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-colors"
                                    title="Eliminar curso"
                                  >
                                    🗑️ Eliminar
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    placeholder="Nombre del curso"
                                    value={course.title || ''}
                                    onChange={(e) => updateEditedData('courses.title', e.target.value, index)}
                                    className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Institución"
                                    value={course.institution || ''}
                                    onChange={(e) => updateEditedData('courses.institution', e.target.value, index)}
                                    className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Período"
                                    value={course.period || ''}
                                    onChange={(e) => updateEditedData('courses.period', e.target.value, index)}
                                    className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none"
                                  />
                                  <textarea
                                    placeholder="Descripción (opcional)"
                                    value={course.description || ''}
                                    onChange={(e) => updateEditedData('courses.description', e.target.value, index)}
                                    className="w-full bg-gray-700 text-white px-2 py-1 rounded text-sm border border-gray-600 focus:border-purple-500 focus:outline-none resize-none"
                                    rows={2}
                                  />
                                </div>
                              </div>
                            ))}
                            {(!editedCvData.courses || editedCvData.courses.length === 0) && (
                              <div className="text-gray-500 text-center py-4">
                                <p className="mb-2">No hay cursos registrados</p>
                                <button
                                  onClick={() => {
                                    const newCourse = { title: '', institution: '', period: '', description: '' };
                                    updateEditedData('courses', [newCourse]);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                >
                                  Añadir primer curso
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Botón para eliminar página completa - Solo mostrar si hay más de una página */}
                  {finalPages && finalPages.length > 1 && (
                    <div className="mt-8 pt-6 border-t border-gray-700">
                      <div className="text-center">
                        <button
                          onClick={() => deletePage(currentEditPage)}
                          className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-6 py-3 rounded-lg transition-all duration-200 flex items-center gap-2 mx-auto shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
                          title={`Eliminar página ${currentEditPage} completa`}
                        >
                          <Trash2 className="w-5 h-5" />
                          <span className="font-medium">Eliminar Página {currentEditPage}</span>
                        </button>
                        <p className="text-gray-400 text-xs mt-2 max-w-md mx-auto">
                          Esta acción eliminará todo el contenido de la página {currentEditPage}.
                          El CV tiene actualmente {finalPages.length} página{finalPages.length !== 1 ? 's' : ''}.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Vista previa en tiempo real */}
              <div className={`${showPreview ? 'block' : 'hidden md:block'} flex-1 bg-gray-100 relative overflow-hidden`}>
                {/* Panel de controles flotante simplificado - SOLO ZOOM IN/OUT */}
                {showPreview && (
                  <div className="md:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-30 bg-gray-800/95 backdrop-blur-sm rounded-xl p-3 shadow-xl border border-gray-600">
                    <div className="flex gap-3 items-center">
                      <button
                        onClick={handleEditModalZoomOut}
                        className="bg-gray-600 hover:bg-gray-700 text-white p-3 rounded-lg transition-colors flex items-center justify-center shadow-md"
                        title="Alejar"
                      >
                        <ZoomOut className="w-5 h-5" />
                      </button>

                      <button
                        onClick={handleEditModalZoomIn}
                        className="bg-gray-600 hover:bg-gray-700 text-white p-3 rounded-lg transition-colors flex items-center justify-center shadow-md"
                        title="Acercar"
                      >
                        <ZoomIn className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Controles de zoom y edición en desktop */}
                <div className="hidden md:block sticky top-0 bg-gray-100 p-3 border-b border-gray-300 z-10">
                  <div className="flex justify-between items-center">
                    <h3 className="text-gray-800 font-medium">Vista Previa</h3>

                    {/* Controles de zoom para desktop */}
                    <div className="flex gap-1">
                      <button
                        onClick={handleEditModalZoomOut}
                        className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors"
                        title="Alejar"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </button>

                      <button
                        onClick={handleEditModalZoomReset}
                        className="bg-gray-800 hover:bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium min-w-[60px] transition-colors"
                        title="Restablecer zoom"
                      >
                        {Math.round(editModalZoom * 100)}%
                      </button>

                      <button
                        onClick={handleEditModalZoomIn}
                        className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors"
                        title="Acercar"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Contenedor con scroll optimizado */}
                <div className="h-full overflow-auto" style={{ height: 'calc(100vh - 140px)' }}>
                  <div
                    className="transition-transform duration-200 ease-in-out min-h-full flex items-start justify-center py-6"
                    style={{
                      transform: `scale(${editModalZoom})`,
                      transformOrigin: 'top center'
                    }}
                  >
                    <div className="bg-white shadow-lg" style={{ width: '595px', minHeight: '842px' }}>
                      {(() => {
                        // Determinar qué experiencias mostrar en la página actual
                        const currentPageExperiences = currentEditPage === 1
                          ? (finalPages[0] || [])
                          : (finalPages[currentEditPage - 1] || []);

                        // Determinar si incluir cursos en esta página
                        const isLastPage = currentEditPage === (finalPages.length || 1);
                        const shouldIncludeCourses = isLastPage && cv.courses && cv.courses.length > 0;

                        return (
                          <div style={{
                            fontFamily: editedCvData.fontFamily || 'Arial, sans-serif',
                            fontSize: `${editedCvData.fontSize || 14}px`
                          }}>
                            <TemplateComponent
                              data={{
                                ...editedCvData,
                                // Solo mostrar nombre y título en la primera página
                                name: currentEditPage === 1 ? editedCvData.name : "",
                                title: currentEditPage === 1 ? editedCvData.title : "",
                                experience: currentPageExperiences,
                                education: currentEditPage === 1 ? editedCvData.education : [],
                                isContinuationPage: currentEditPage > 1,
                                courses: shouldIncludeCourses ? editedCvData.courses : [],
                                fontFamily: editedCvData.fontFamily,
                                fontSize: editedCvData.fontSize
                              } as any}
                              customColor={editedCvData.templateColor}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </>
          </div>
        </div>
      )}
      {/* --- FIN MODAL CARTA --- */}

      {/* --- MODAL DE EDICIÓN DE CARTA DE PRESENTACIÓN --- */}
      {isEditLetterModalOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex flex-col">
          {/* Header del modal */}
          <div className="bg-gradient-to-r from-purple-900 to-blue-900 border-b border-purple-500/30 p-3 md:p-4">
            <div className="text-center mb-3 md:mb-4">
              <h2 className="text-lg md:text-xl font-bold text-white flex items-center justify-center gap-2">
                <Edit3 className="w-5 h-5 md:w-6 md:h-6" />
                Editando Carta de Presentación
              </h2>
              <p className="text-purple-200 text-xs md:text-sm">
                Personaliza tu carta de presentación
              </p>
            </div>

            {/* Botón Ver Vista Previa en móvil - MOVIDO AQUÍ ARRIBA */}
            <div className="md:hidden mb-3">
              <button
                onClick={() => setShowLetterPreview(!showLetterPreview)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {showLetterPreview ? 'Volver a Editar' : 'Ver Vista Previa'}
              </button>
            </div>

            {/* Controles de acción */}
            <div className="flex items-center justify-center gap-3">
              {isLetterUnsavedChanges && (
                <span className="bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded-full text-xs border border-yellow-500/30">
                  Sin guardar
                </span>
              )}

              <button
                onClick={handleSaveLetterChanges}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
              >
                <Save className="w-4 h-4" />
                Guardar Cambios
              </button>

              <button
                onClick={closeEditLetterModal}
                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Contenido del modal */}
          <div className="flex flex-1 overflow-hidden">
            {/* Panel de edición */}
            <div className={`${showLetterPreview ? 'hidden md:block' : 'block'} w-full md:w-1/2 bg-gray-900 border-r border-purple-500/20 overflow-y-auto`}>
              <div className="p-4 pb-20">  {/* Agregado pb-20 para padding inferior */}
                {/* Sección de Datos Personales */}
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Datos Personales
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">
                        Nombre Completo
                      </label>
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => updatePersonalData('name', e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                        placeholder="Tu nombre completo"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">
                        Título Profesional
                      </label>
                      <input
                        type="text"
                        value={editedTitle}
                        onChange={(e) => updatePersonalData('title', e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                        placeholder="Ej: Data Scientist & AI"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">
                        Ubicación
                      </label>
                      <input
                        type="text"
                        value={editedLocation}
                        onChange={(e) => updatePersonalData('location', e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                        placeholder="Ej: Ñuñoa, Santiago de Chile"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">
                        Teléfono
                      </label>
                      <input
                        type="text"
                        value={editedPhone}
                        onChange={(e) => updatePersonalData('phone', e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                        placeholder="Ej: +569 2601 6816"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editedEmail}
                        onChange={(e) => updatePersonalData('email', e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-purple-500 focus:outline-none"
                        placeholder="tu.email@ejemplo.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Sección de Contenido de la Carta */}
                <div className="bg-gray-800 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Contenido de la Carta
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">
                        Texto de la carta (separar párrafos con líneas vacías)
                      </label>
                      <textarea
                        value={editedCoverLetter}
                        onChange={(e) => updateCoverLetter(e.target.value)}
                        className="w-full bg-gray-700 text-white px-3 py-3 rounded border border-gray-600 focus:border-purple-500 focus:outline-none resize-none"
                        rows={15}
                        placeholder="Escribe aquí el contenido de tu carta de presentación..."
                      />
                    </div>

                    {/* Contador de caracteres */}
                    <div className="text-gray-400 text-xs text-right">
                      {editedCoverLetter.length} caracteres
                    </div>

                    {/* Consejos */}
                    <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                      <h4 className="text-blue-300 font-medium mb-2">💡 Consejos para una buena carta:</h4>
                      <ul className="text-blue-200 text-sm space-y-1">
                        <li>• Personaliza para cada empresa y puesto</li>
                        <li>• Menciona logros específicos y cuantificables</li>
                        <li>• Mantén un tono profesional pero cercano</li>
                        <li>• No repitas exactamente lo que dice tu CV</li>
                        <li>• Termina con una llamada a la acción clara</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vista previa */}
            <div className={`${showLetterPreview ? 'block' : 'hidden md:block'} flex-1 bg-gray-100 overflow-auto`}>
              <div className="sticky top-0 bg-gray-100 p-3 border-b border-gray-300 z-10">
                <h3 className="text-gray-800 font-medium">Vista Previa de la Carta</h3>
              </div>

              <div className="p-6">
                <div className="bg-white shadow-lg p-8 max-w-2xl mx-auto">
                  {/* Renderizar vista previa de la carta */}
                  {(() => {
                    if (!editedCoverLetter.trim()) {
                      return (
                        <div className="text-gray-500 text-center py-12">
                          Escribe contenido para ver la vista previa...
                        </div>
                      );
                    }

                    const currentDate = new Date().toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    });

                    const paragraphs = editedCoverLetter.split('\n\n').filter(p => p.trim() !== '');

                    return (
                      <div>
                        {/* Encabezado */}
                        <div className="border-b border-gray-200 pb-6 mb-10">
                          <div className="flex justify-between items-start">
                            <div className="space-y-3">
                              <h1 className="text-2xl font-bold text-purple-700">
                                {editedName || 'Tu Nombre'}
                              </h1>
                              <p className="text-gray-700 font-medium">
                                {editedTitle || 'Tu Título Profesional'}
                              </p>
                              <div className="text-sm text-gray-600 space-y-1">
                                <p>{editedLocation || 'Tu ubicación'}</p>
                                <p>{editedPhone || 'Tu teléfono'}</p>
                                <p>{editedEmail || 'Tu email'}</p>
                              </div>
                            </div>
                            <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-2 rounded-lg">
                              <p className="text-gray-600 text-right font-medium">{currentDate}</p>
                            </div>
                          </div>
                        </div>

                        {/* Contenido */}
                        <div className="space-y-6 text-gray-700 leading-relaxed">
                          <p className="text-gray-800 font-medium text-lg">
                            Estimado/a Equipo de Recursos Humanos:
                          </p>

                          <div className="space-y-5">
                            {paragraphs.map((paragraph, index) => (
                              <p key={index} className="text-justify">
                                {paragraph}
                              </p>
                            ))}
                          </div>

                          {/* Firma */}
                          <div className="mt-12 pt-6 border-t border-gray-200">
                            <p className="text-gray-800 font-medium">Atentamente,</p>
                            <div className="mt-5">
                              <div className="inline-block bg-purple-50 px-4 py-2 rounded">
                                <p className="font-bold text-purple-700">
                                  {editedName || 'Tu Nombre'}
                                </p>
                                <p className="text-gray-700">
                                  {editedTitle || 'Tu Título Profesional'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* --- FIN MODAL CARTA --- */}

      {/* --- CROPPER DE IMAGEN --- */}
      {showCropper && (
        <ImageCropper
          imageSrc={tempImage}
          onCancel={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}
      {/* --- FIN CROPPER --- */}
    </div>
  );
}
